// ── Task detail slide-out panel ──

import { api } from '../api.js';
import { getUser } from '../auth.js';
import { can, effectiveRole, roleLabel } from '../lib/permissions.js';
import { showToast, openModal, closeModal } from '../ui.js';
import { escapeHtml, renderMarkdown, formatDate, renderAvatarHtml, TYPES, TYPE_COLORS, TYPE_LABELS, getTypeIcon, sprintOptionsHtml, nextSprintNumber, canStartTask, availableStatuses, debounce } from '../utils.js';
import { openCreateTaskModal } from './create-task-modal.js';
import { renderParentContext, renderEpicContext } from './parent-context.js';
import { renderMemberCombo, createMemberCombo } from './member-combo.js';
import { attachImageHandlers } from '../lib/image-paste.js';
import { attachEmbedControls, closeAnyOpenEmbedMenu } from '../lib/embed-menu.js';
import { navigate, getNavigationId } from '../router.js';
import { loadStatuses, statusNames, statusLabel, statusBadgeAttrs, roleStatus, closedNames } from '../lib/statuses.js';
import { URGENCY_VALUES, urgencyCopy, urgencyLabel, renderUrgencyBadge, syncUrgencyBadges } from '../lib/task-urgency.js';
import { taskDueState, parseDayInput, formatDay } from '../lib/due-tasks.js';
import { sprintsEnabled } from '../lib/sprints-enabled.js';
import { groupLabelSingular } from '../lib/task-group-label.js';
import { t } from '../lib/i18n.js';
import { isPermissionMode, permissionModeLabel, projectDefaultOptionLabel, permissionModeIcon, renderPermissionModeSelect, bindPermissionModeSelects, syncPermissionModeSelect, effectivePermissionModeHint, isCowboyAllowed, COWBOY_MODES, normalizeCowboyScope } from '../lib/agent-permission-mode.js';
import { loadProject, invalidateProject } from '../lib/project.js';
import { loadDevices, loadOnlineDevices, reloadDevices } from '../lib/devices.js';
import { buildStartTaskUrl } from '../lib/task-app-link.js';
import { rawOffsetFromRendered } from '../lib/caret-map.js';
import { attachTagTypeahead } from '../lib/tag-typeahead.js';
import { getSeenEventId, setSeenEventId, unreadEventIds, maxEventId } from '../lib/task-events-seen.js';
import { highlightMentions } from '../lib/mention-highlight.js';
import { renderSafeMarkdown } from '../lib/safe-markdown.js';
import { attachMentionTypeahead, closeAnyOpenMentionTypeahead } from '../lib/mention-typeahead.js';
import { focusBoardCard, clearBoardFocus, findTaskCard } from '../lib/hover-expand.js';
import { PARENT_PICKER_LIMIT, parentPickerPath, parentPickerCandidates } from '../lib/parent-picker.js';
import { noteFocusBeforeRender, disableForSave } from '../lib/autosave-focus.js';
import { deviceSessionClient as relayClient } from '../lib/device-session-client.js';
import { createDeviceSessionScope, applyDeviceSessionEvent, renderDeviceAttribution, deviceFieldLabel, syncDeviceBadges } from '../lib/device-attribution.js';
import { bindRemoteTerminal, safeTerminalLink, terminalCanStop, terminalCanType, terminalStatus } from '../lib/remote-terminal-session.js';
import { remoteText, remoteCodeText } from '../lib/remote-session-copy.js';
import { agentLabel, canRunOnDevice, isLiveSession, remoteControlsModel, startableDevices } from '../lib/remote-session-model.js';
import { loadXterm } from '../lib/xterm-loader.js';
import { renderDevLinks, bindDevLinks } from './task-dev-links.js';
import { attachDependencyTypeahead, renderDependencyChips } from './dependency-typeahead.js';
import {
  taskEditableFields, AGENT_ASSIGNEES, EFFORT_LEVELS, MODEL_SUGGESTIONS, agentModelField, agentHasEffort,
  agentHasDesignMode, agentLabel as taskAgentLabel, effortLabel, modelInputValue, dependencyFloor, blockingDependencyResults,
  dependencyProblemText, dependencyCheckPath,
} from '../lib/task-fields.js';

// (TPT9213) The writable task fields both task forms offer — this drawer/page and the New Task
// modal (components/create-task-modal.js). Defined in lib/task-fields.js; re-exported here, the
// edit surface it describes.
export { taskEditableFields };

let panelEl = null;
let overlayEl = null;
let escHandler = null;
let currentState = null;
let comments = [];
let assigneeCombo = null;
let tagTypeahead = null;
// (TPT9213) Depends on chip editor — same destroy-and-recreate discipline as tagTypeahead.
let depTypeahead = null;
// Dependency saves, chained so their PATCHes reach the API in the order they were made (the due
// date's discipline, see dueSaveChain below).
let depSaveChain = Promise.resolve();
// TPT33 — @mention typeahead on the compose box (recreated every paint, same
// destroy-and-recreate discipline as assigneeCombo/tagTypeahead above) plus the
// single in-place inline comment editor (at most one open at a time — module globals
// for the same reason `comments` is: refreshPanelContent() replaces the pane content,
// so anything mid-edit has to live outside that DOM to survive/be reset).
let commentMentionTypeahead = null;
let editingCommentId = null;
let editCommentMentionTypeahead = null;
// Bumped on every openTaskDetail() and snapshotted by closeTaskDetail()'s deferred onClose
// timer (C1261) — if a new panel opened before that timer fires (close card A, open card B
// within the 220ms window), the sequence no longer matches and the stale onClose is dropped
// instead of firing a page-owner reload that would force-close the freshly opened panel B.
let panelSeq = 0;

// Owner token of the open drawer (TPT647): openTaskDetail() returns a fresh one and
// closeTaskDetail(token) acts only while it still owns the drawer, so a page teardown that runs
// late cannot close the drawer the page now on screen has opened. Child/parent navigation inside
// the drawer reopens under the same token, so the page that opened it can still close it.
let detailOwner = null;
// Owner token of the modal this drawer (or the standalone page) opened — Select Parent, Delete
// Task, Add Subtask. Closed with the drawer and by mountTaskDetail()'s unmount, never by a page.
let detailModalToken = null;

// Board-focus state (TPT378): `{ board, taskKey }` while a board card is focused behind the
// drawer, else null. Set by openTaskDetail() from its `focusCard`/`focusBoard` options and
// cleared by closeTaskDetail(), so the blur never outlives the drawer. Module-global for the
// same reason as `comments`: a board re-render rewrites the card DOM, so what to re-mark has
// to live outside it (see restoreBoardFocus()).
let boardFocus = null;

// Focus `card` on `board` and dim the overlay to the lighter board-focus tint. Always releases
// any previous focus first; with no card/board (every non-board caller) the overlay just gets
// its plain full-screen tint back.
function applyBoardFocus(card, board) {
  releaseBoardFocus();
  // The tint modifier is dropped here, on the next open, never in releaseBoardFocus(): the
  // overlay only transitions `opacity`, so dropping it on close would snap the background from
  // the light to the full tint while the overlay is still fading out.
  overlayEl.classList.remove('task-detail-overlay--board-focus');
  if (!card || !board) return;
  focusBoardCard(card, board);
  overlayEl.classList.add('task-detail-overlay--board-focus');
  boardFocus = { board, taskKey: card.dataset.taskKey };
}

// Lift the blur + ring off the board. Leaves the overlay's tint modifier alone (see above).
function releaseBoardFocus() {
  if (!boardFocus) return;
  clearBoardFocus(boardFocus.board);
  boardFocus = null;
}

// Re-mark the focused card after `board`'s own re-render replaced its card DOM (TPT378).
// No-op unless this drawer is holding focus on that exact board element; if the card is gone
// from the new render (filtered out, deleted) the board simply stays blurred until close.
export function restoreBoardFocus(board) {
  if (!boardFocus || boardFocus.board !== board) return;
  focusBoardCard(findTaskCard(board, boardFocus.taskKey), board);
}

// ── Notifications tab (TPT18) ── module globals, same lifecycle discipline as `comments`
// above: renderPanelContent()/refreshPanelContent() replace the rendered panes,
// so anything that must survive a repaint (which tab is active, which rows
// have already scrolled into view this session) has to live outside the DOM.
let taskEvents = [];             // newest-first, as returned by GET .../events
let eventsSubscribed = true;     // this user's effective `subscribed` state
let activeTab = 'details';       // 'details' | 'comments' | 'notifications'
let observedEventIds = new Set(); // event ids the IntersectionObserver has already seen this session
let eventObserver = null;

// ── Remote sessions (TPT495) ── The picker and session rows for the task on screen, and the one
// terminal dialog. `remoteView` lives outside the DOM like `comments`: its section repaints alone
// on every relay event, and refreshPanelContent() repaints it from here. Null while the task on
// screen has no remote section at all. See startRemoteControls().
let attributionView = null;
let remoteView = null;
let remoteTerminal = null;
// `device` frames only say that something changed, and a device reconnecting fires them in pairs.
const DEVICE_REFRESH_MS = 1000;
// Relay link states a reopened link recovers from (lib/device-relay-client.js `connection`).
const RELAY_RECOVERING = new Set(['reconnecting', 'auth', 'stopped']);
// A started session opens its terminal by itself only this soon: a run the device queued for
// minutes must not pop a dialog over whatever the user has moved on to.
const AUTO_ATTACH_WINDOW_MS = 20000;
// xterm paints its own palette, so this does not follow the 21 app palettes: agent TUIs assume a
// dark ground. These are the brand `ink` tokens (styles.css).
const TERMINAL_THEME = {
  background: '#141413', foreground: '#f4f1ea', cursor: '#14b8a6', cursorAccent: '#141413',
  selectionBackground: 'rgba(20,184,166,0.32)',
  black: '#35352f', red: '#e8897a', green: '#a9cf8f', yellow: '#e3c877',
  blue: '#8aafcb', magenta: '#c9b0c6', cyan: '#7fd6c6', white: '#d8d3c6',
  brightBlack: '#8c8c82', brightRed: '#f0a598', brightGreen: '#c1e0ab', brightYellow: '#efdb9d',
  brightBlue: '#a9c6dc', brightMagenta: '#dcc9da', brightCyan: '#a5e6da', brightWhite: '#f4f1ea',
};

// Due date saves (TPT589), chained so their PATCHes reach the API in the order they were made:
// Chrome fires `change` on every keystroke that completes a date, so typing one can send several in
// quick succession. `duePending` counts the queued and running ones (see queueDueSave()).
let dueSaveChain = Promise.resolve();
let duePending = 0;

function ensurePanel() {
  if (panelEl && overlayEl) return;

  overlayEl = document.createElement('div');
  overlayEl.className = 'task-detail-overlay';
  overlayEl.addEventListener('click', () => closeTaskDetail());

  panelEl = document.createElement('div');
  panelEl.className = 'task-detail-panel';

  document.body.appendChild(overlayEl);
  document.body.appendChild(panelEl);
}

// ── Compact phone layout (TPT9214) ──
// On a phone the drawer covers the screen, and the desktop arrangement (key + Type header, then
// title, Start in Task App and the device section, then the tab strip, all above one small pane
// scroller, with the footer pinned under it) would leave a sliver of the form on screen: about
// 50px of a 390×664 iPhone viewport, none on its side. The compact layout makes the whole drawer
// one scroller. Type, title, launch controls and the footer scroll with the form; only the key/Close
// bar and the tab strip stay pinned, each only while the pinned chrome fits MOBILE_CHROME_BUDGET
// of the visible height. styles.css § Compact phone layout holds the rules; the classes come from
// syncMobileEditorLayout() below.
export const MOBILE_EDITOR_MAX_WIDTH = 600;  // styles.css' full-width drawer breakpoint
export const MOBILE_EDITOR_MAX_HEIGHT = 500; // a phone on its side
export const MOBILE_BAR_HEIGHT = 48;         // --td-bar-h in styles.css
export const MOBILE_TABS_HEIGHT = 44;        // the compact tab strip
export const MOBILE_CHROME_BUDGET = 0.15;    // pinned chrome stays within 15% of the visible height
// A software keyboard takes at least this much of the visual viewport; an iPad's shortcut bar or a
// collapsing browser toolbar takes less.
export const MOBILE_KEYBOARD_MIN = 150;

const COMPACT_CLASSES = {
  compact: 'task-detail-panel--compact',
  keyboard: 'task-detail-panel--keyboard',
  pinBar: 'task-detail-panel--pin-bar',
  pinTabs: 'task-detail-panel--pin-tabs',
};

// The drawer's layout for one viewport. Pure; exported as a test seam.
//   mode          'panel' | 'page'. Only the drawer has fixed chrome to shed: the page scrolls with
//                 the document.
//   width/height  the layout viewport, which a phone keyboard leaves alone (iOS Safari, Android
//                 Chrome since 108). syncMobileEditorLayout() passes its largest reading.
//   visualHeight, visualTop, scale  window.visualViewport: the part actually on screen.
// Returns { compact, keyboard, pinBar, pinTabs, box }:
//   compact   width ≤ MOBILE_EDITOR_MAX_WIDTH or height ≤ MOBILE_EDITOR_MAX_HEIGHT.
//   keyboard  a software keyboard covers the bottom of the layout viewport: the visual viewport is
//             at least MOBILE_KEYBOARD_MIN shorter and not pinch-zoomed. `box` ({ top, height })
//             then fits the drawer to the visible area above the keyboard, so the last field and
//             the final actions can still scroll into view.
//   pinBar    the 48px key/Close bar stays on screen while the form scrolls.
//   pinTabs   the tab strip sticks under the bar too.
export function mobileTaskEditorLayout({ mode = 'panel', width, height, visualHeight, visualTop = 0, scale = 1 } = {}) {
  const layout = { compact: false, keyboard: false, pinBar: false, pinTabs: false, box: null };
  if (mode !== 'panel' || !(width > 0) || !(height > 0)) return layout;
  if (width > MOBILE_EDITOR_MAX_WIDTH && height > MOBILE_EDITOR_MAX_HEIGHT) return layout;
  layout.compact = true;
  const zoomed = Math.abs((Number(scale) || 1) - 1) > 0.01;
  layout.keyboard = !zoomed && visualHeight > 0 && height - visualHeight >= MOBILE_KEYBOARD_MIN;
  const budget = (layout.keyboard ? visualHeight : height) * MOBILE_CHROME_BUDGET;
  layout.pinBar = budget >= MOBILE_BAR_HEIGHT;
  layout.pinTabs = layout.pinBar && budget >= MOBILE_BAR_HEIGHT + MOBILE_TABS_HEIGHT;
  if (layout.keyboard) layout.box = { top: Math.max(0, Number(visualTop) || 0), height: visualHeight };
  return layout;
}

let mobileLayout = null;        // what syncMobileEditorLayout() last applied
let mobileLayoutViewport = null; // the visualViewport its listeners are bound to
let mobileLayoutBound = false;

function isCompactDrawer() {
  return Boolean(panelEl && panelEl.classList.contains(COMPACT_CLASSES.compact));
}

// Applies mobileTaskEditorLayout() for the current viewport to the drawer: the classes, and the
// visible-area box while a keyboard is open. Runs on open and on every viewport change while open.
function syncMobileEditorLayout() {
  if (!panelEl) return;
  const vv = window.visualViewport;
  // The layout viewport at its largest reading: iOS Safari's innerWidth/innerHeight can follow a
  // pinch zoom or the keyboard, the root element's client size cannot; Chrome's inner size counts
  // the scrollbar, as media queries do.
  const root = document.documentElement;
  const next = mobileTaskEditorLayout({
    mode: 'panel',
    width: Math.max(window.innerWidth || 0, root?.clientWidth || 0),
    height: Math.max(window.innerHeight || 0, root?.clientHeight || 0),
    visualHeight: vv ? vv.height : undefined,
    visualTop: vv ? vv.offsetTop : 0,
    scale: vv ? vv.scale : 1,
  });
  for (const [key, className] of Object.entries(COMPACT_CLASSES)) panelEl.classList.toggle(className, next[key]);
  const style = panelEl.style;
  if (style && typeof style.setProperty === 'function') {
    if (next.box) {
      style.setProperty('--td-vv-top', `${next.box.top}px`);
      style.setProperty('--td-vv-h', `${next.box.height}px`);
    } else {
      style.removeProperty('--td-vv-top');
      style.removeProperty('--td-vv-h');
    }
  }
  const shrunk = next.keyboard && !(mobileLayout && mobileLayout.keyboard && mobileLayout.box.height === next.box.height);
  mobileLayout = next;
  // The keyboard came up over the field being typed in: bring it back into the shorter drawer.
  if (shrunk) revealFocusedField();
}

// Binds (on) or releases syncMobileEditorLayout() on the viewport events. The visual viewport moves
// on its own while a keyboard is open (iOS pans it to the focused field), so its scroll counts too.
function bindMobileEditorLayout(on) {
  if (on === mobileLayoutBound) return;
  mobileLayoutBound = on;
  const method = on ? 'addEventListener' : 'removeEventListener';
  window[method]?.('resize', syncMobileEditorLayout);
  window[method]?.('orientationchange', syncMobileEditorLayout);
  const vv = on ? window.visualViewport : mobileLayoutViewport;
  mobileLayoutViewport = on ? vv || null : null;
  vv?.[method]?.('resize', syncMobileEditorLayout);
  vv?.[method]?.('scroll', syncMobileEditorLayout);
}

// Height of the chrome pinned over the compact drawer's top edge right now: the bar, plus the tab
// strip once it has stuck under it.
function pinnedChromeHeight() {
  const view = panelEl.getBoundingClientRect();
  let cover = 0;
  for (const selector of ['.task-detail-key-row', '.task-detail-tabs']) {
    const el = panelEl.querySelector(selector);
    if (!el || getComputedStyle(el).position !== 'sticky') continue;
    const box = el.getBoundingClientRect();
    if (box.top <= view.top + cover + 1) cover = Math.max(cover, box.bottom - view.top);
  }
  return cover;
}

// Scrolls the compact drawer (the drawer itself is the scroller there) so the focused field sits in
// its visible part, below the pinned chrome. A field taller than that space shows its top.
function revealFocusedField() {
  if (!isCompactDrawer()) return;
  const field = document.activeElement;
  if (!field || field === panelEl || !panelEl.contains(field)) return;
  const view = panelEl.getBoundingClientRect();
  const box = field.getBoundingClientRect();
  const top = view.top + pinnedChromeHeight() + 8;
  const bottom = view.bottom - 8;
  if (box.top < top || box.height > bottom - top) panelEl.scrollTop -= top - box.top;
  else if (box.bottom > bottom) panelEl.scrollTop += box.bottom - bottom;
}

// The gap the compact body keeps between the tab strip and the pane (styles.css, 1rem).
const COMPACT_PANE_GAP = 16;

// A tab switch in the compact drawer keeps the scroll position, unless the old pane had scrolled up
// under the pinned tab strip: then the new pane starts just below the strip.
function alignCompactPane(content) {
  const tabs = panelEl.querySelector('.task-detail-tabs');
  if (!content || !tabs) return;
  const gap = content.getBoundingClientRect().top - tabs.getBoundingClientRect().bottom;
  if (gap < COMPACT_PANE_GAP) panelEl.scrollTop -= COMPACT_PANE_GAP - gap;
}

// What the dropdown helpers (member combo, tag/dependency/mention typeaheads, Embed menu) listen to
// for `scroll`: the document for the page, the drawer itself for the panel. They listen in the
// capture phase, so the drawer also hears its inner pane scroller (.task-detail-tab-content), the
// one that scrolls in the desktop layout. The compact layout scrolls the drawer itself (TPT9214).
function detailScrollEl(state) {
  return state.mode === 'page' ? window : state.root;
}

// (TPT18) task_events.event_type -> display label. Falls back to the raw string in
// renderPanelContent() so a future server-side event type still renders instead of
// disappearing — this map is a display nicety, not a whitelist.
const EVENT_TYPE_LABELS = {
  task_created: 'Created',
  status_changed: 'Status',
  assigned: 'Assigned',
  comment: 'Comment',
};

// The drawer's Sprint select always offers Backlog (value 0), selected for a backlog task
// (priority 0 or unset), so a writer sees the same value a viewer's static field shows and can
// move any task back to the backlog. sprintOptionsHtml() itself never emits 0: its other callers
// (backlog bulk move, create modal) rely on that.
export function getSprintOptions(currentPriority, sprints, allTasks, label) {
  const next = nextSprintNumber(sprints, allTasks);
  return sprintOptionsHtml(sprints, allTasks, currentPriority, label)
    + `<option value="0" ${currentPriority > 0 ? '' : 'selected'}>Backlog</option>`
    + `<option value="${next}">+ New ${escapeHtml(label)}</option>`;
}

// Rows whose `priority` names the sprint numbers in use, for the Sprint select and Add Child's
// modal: `{ priority }` rows for every in-use number, built from GET /tasks/facets `priorities`.
// The standalone page (TPT618), the board (TPT628) and the backlog pass them as
// options.sprintRows; for every other caller openTaskDetail() fetches them itself into
// state.sprintRows (TPT641, loadSprintRows()). Without either, or with an empty list (a failed
// facets call), the caller's own rows stand in.
function sprintRowsOf(state) {
  const nonEmpty = rows => Array.isArray(rows) && rows.length > 0;
  if (nonEmpty(state.options.sprintRows)) return state.options.sprintRows;
  if (nonEmpty(state.sprintRows)) return state.sprintRows;
  return state.options.allTasks || [];
}

// Project-wide `{ priority }` rows from GET /tasks/facets `priorities` for a caller that passed
// no options.sprintRows (TPT641) — its own rows are only part of the project (an epic and its
// children, a top-level page, a paginated cross-project page). Resolves null without a request
// when the caller supplied them, and null on any failure (never throws). Exported as a test seam.
export async function loadSprintRows(projectId, options) {
  if (Array.isArray(options.sprintRows) && options.sprintRows.length) return null;
  const res = await api.get(`/projects/${projectId}/tasks/facets`).catch(() => null);
  return res && Array.isArray(res.priorities) ? res.priorities.map(priority => ({ priority })) : null;
}

// The open task's children, read by its key (TPT628) — never derived from the caller's rows,
// which on a board are its filtered page. Full rows (story_points included); no grandchild
// rollup. Resolves null on any failure, never throws. Exported as a test seam.
export async function loadChildren(projectId, taskKey) {
  const res = await api.get(`/projects/${projectId}/tasks/${encodeURIComponent(taskKey)}/children?include_children_count=false`).catch(() => null);
  return res && Array.isArray(res.children) ? res.children : null;
}

// The children list to render: the fetched rows when loadChildren() succeeded, else whatever the
// caller's rows hold (soft fail). Pure; exported as a test seam.
export function detailChildren(fetched, rows, taskId) {
  return Array.isArray(fetched) ? fetched : (rows || []).filter(t => t.parent_id === taskId);
}

// descriptionHtml(text, members) / commentBodyHtml(content, members) (TPT33) — the
// single rendering path for "raw markdown -> highlighted HTML" used by every
// description/comment render site in this file (renderPanelContent()'s initial paint,
// startDescriptionEdit()'s save(), and renderCommentRow() below). highlightMentions()
// is NOT idempotent, so it must only ever wrap a FRESH render call — never
// re-applied to already-highlighted output — which is why every call site goes
// through these two helpers instead of calling the renderer+highlightMentions()
// inline. Descriptions use renderMarkdown(); comment bodies (TPT359) go through the
// sandboxed renderSafeMarkdown() instead — raw HTML is escaped to inert text and only
// http(s)/relative link + image URLs survive (lib/safe-markdown.js).
function descriptionHtml(text, members) {
  return text
    ? highlightMentions(renderMarkdown(text), members)
    : '<span class="text-muted">No description</span>';
}

function commentBodyHtml(content, members) {
  return highlightMentions(
    renderSafeMarkdown((content || '').replace(/<!--\s*trello-action:[^\s>]*\s*-->/g, '')),
    members
  );
}

// renderCommentRow(c, members) (TPT33) — the single comment-row renderer, shared by
// the full paint (renderPanelContent(), comments.map() above) and the post-edit
// in-place DOM swap (saveCommentEdit()/cancelCommentEdit() below) so the two paths
// can never drift. `canEdit` mirrors the PATCH route's own author-only 403 check
// (routes/tasks.js) — the Edit button only ever appears where the server would also
// accept the edit. `updated_at` (TPT31, NULL = never edited) drives the "(edited)"
// marker.
function detailCan(cap) {
  return can(currentState?.project, getUser(), cap);
}

function renderCommentRow(c, members) {
  const me = getUser();
  const canEdit = detailCan('comment:write') && !!(me && c.user && c.user.id === me.id);
  return `
                <div class="task-detail-comment" data-comment-id="${Number(c.id)}" data-comment-type="${escapeHtml(c.comment_type || 'comment')}">
                  <div class="task-detail-comment-meta">
                    ${c.comment_type === 'resolution' ? '<span class="badge-resolution">Resolution</span>' : ''}
                    <strong>${escapeHtml(c.user?.name || c.user?.email || 'Unknown')}</strong>
                    <span class="text-muted text-sm">${formatDate(c.created_at)}</span>
                    ${c.updated_at ? `<span class="task-detail-comment-edited" title="Edited ${escapeHtml(formatDate(c.updated_at))}">(edited)</span>` : ''}
                    ${canEdit ? '<button type="button" class="btn btn-ghost btn-sm task-detail-comment-edit-btn">Edit</button>' : ''}
                  </div>
                  <div class="task-detail-comment-body">${commentBodyHtml(c.content, members)}</div>
                </div>`;
}

export { renderEpicContext };

// Resolves the open task's parent when the caller's rows don't hold it — a filtered board can
// omit it, and the standalone page holds only the task's children. One single-row read by the
// parent_key the task's own GET /:taskKey row carries (TPT618), never a project-wide list.
// Stored as options.parentTask (the caller's array is left alone); renderPanelContent() shows
// it only while its id still matches task.parent_id. Never throws: a failed lookup just leaves
// the parent unshown. Exported as a test seam.
export async function loadParentContext(projectId, task, options) {
  if (!task.parent_id || options.allTasks?.some(t => t.id === task.parent_id)) return;
  if (!task.parent_key) return;
  const res = await api.get(`/projects/${projectId}/tasks/${encodeURIComponent(task.parent_key)}`).catch(() => null);
  const parent = res && res.task;
  if (parent && parent.id === task.parent_id) options.parentTask = parent;
}

// The Due date field (TPT589): a native date input plus a clear button for a writer, the plain
// date for a viewer, and under either the state pill — red `.overdue` while an open task is past
// its date, the countdown otherwise, nothing for an undated task or a closed status
// (taskDueState(), lib/due-tasks.js). syncDueField() keeps the button and the pill current after a
// save without repainting.
function renderDueField(task, canWrite, closed) {
  const due = task.due_date || '';
  const state = taskDueState(task, closed);
  const control = canWrite
    ? `<div class="task-detail-due-control">
              <input type="date" class="form-input task-detail-due-input" aria-label="Due date" max="9999-12-31" value="${escapeHtml(due)}">
              <button type="button" class="btn btn-ghost btn-sm task-detail-due-clear" title="Clear due date" aria-label="Clear due date"${due ? '' : ' hidden'}>&times;</button>
            </div>`
    : `<div class="task-detail-static">${due ? escapeHtml(formatDay(due)) : '<span class="text-muted">—</span>'}</div>`;
  return `
          <div class="task-detail-field task-detail-due-field" data-field="due_date">
            <label class="task-detail-field-label">Due date</label>
            ${control}
            <span class="task-detail-due-state${state && state.overdue ? ' overdue' : ''}"${state ? '' : ' hidden'}>${state ? escapeHtml(state.label) : ''}</span>
          </div>`;
}

// The Permission mode field (TPT9063): a writer picks an explicit agent_permission_mode or
// "Project Default (<inherited>)" (value "", saved as an explicit null — an omitted field would
// keep the override); a viewer reads the same label as text. Under either, the hint explains the
// EFFECTIVE mode — the override, else the project default the task inherits when it starts.
// `project` is currentState.project (loadProject(), fails open to null → "not set"); it is a
// parameter only so the test seam needs no module state. syncPermissionModeHint() re-derives
// the hint after a save without repainting. The Cowboy-coding mode card sits under the hint.
export function renderTaskPermissionMode(task, canWrite, project = currentState?.project) {
  const value = isPermissionMode(task.agent_permission_mode) ? task.agent_permission_mode : null;
  const inherited = project?.agent_permission_mode ?? null;
  const control = canWrite
    ? renderPermissionModeSelect(value, { id: 'task-detail-permission-mode', className: 'task-detail-agent-permission-mode-select', projectDefault: { inherited } })
    : `<div class="task-detail-static">${permissionModeIcon(value)}${value ? permissionModeLabel(value) : projectDefaultOptionLabel(inherited)}</div>`;
  return `
          <div class="task-detail-field task-detail-permission-field" data-field="agent_permission_mode">
            <label class="task-detail-field-label">Permission mode</label>
            ${control}
            <span class="text-muted text-sm task-detail-permission-hint">${effectivePermissionModeHint(value, inherited)}</span>
            ${renderCowboyMode(task, canWrite, inherited)}
          </div>`;
}

// Scope configuration applies to future launches. Outside Bypass it renders Disabled and
// stays hidden; saved responses remain authoritative for both writers and viewers.
function renderCowboyMode(task, canWrite, inherited) {
  const allowed = isCowboyAllowed(task.agent_permission_mode ?? null, inherited);
  const scope = allowed ? normalizeCowboyScope(task.cowboy_mode) : 'disabled';
  const on = scope !== 'disabled';
  if (canWrite) {
    return `<label class="task-detail-cowboy"${allowed ? '' : ' hidden'}>
              <select class="form-select task-detail-cowboy-toggle" data-field="cowboy_mode" aria-labelledby="task-detail-cowboy-label" aria-describedby="task-detail-cowboy-hint">${COWBOY_MODES.map(mode => `<option value="${mode}"${scope === mode ? ' selected' : ''}>${t(`cowboyMode.${mode}`)}</option>`).join('')}</select>
              <span class="task-detail-cowboy-label" id="task-detail-cowboy-label">${t('cowboyMode.label')}</span>
              <span class="task-detail-cowboy-hint" id="task-detail-cowboy-hint">${t('cowboyMode.hint')}</span>
            </label>`;
  }
  if (!allowed) return '';
  return `<div class="task-detail-cowboy task-detail-cowboy--static${on ? ' task-detail-cowboy--on' : ''}">
              <span class="task-detail-cowboy-label">${t('cowboyMode.label')}</span>
              <span class="task-detail-cowboy-state">${t(`cowboyMode.${scope}`)}</span>
              <span class="task-detail-cowboy-hint">${t('cowboyMode.hint')}</span>
            </div>`;
}

// The Agent run section (TPT9213): how the Task App runs the task — its agent, that agent's model,
// reasoning effort (Claude, Codex), Claude Design mode, and the Permission mode with its Cowboy
// scope. Controls that only apply to another agent render hidden, so an agent change reveals them
// in place (syncAgentFields()); their stored values are kept. A viewer reads the same values.
function renderAgentSection(task, canWrite) {
  const agent = AGENT_ASSIGNEES.includes(task.agent_assignee) ? task.agent_assignee : null;
  const attr = s => escapeHtml(s).replace(/"/g, '&quot;');
  const staticField = inner => `<div class="task-detail-static">${inner}</div>`;
  const muted = text => `<span class="text-muted">${escapeHtml(text)}</span>`;
  const field = (key, label, control, { hidden = false, forId = '' } = {}) => `
          <div class="task-detail-field" data-field="${key}"${hidden ? ' hidden' : ''}>
            <label class="task-detail-field-label"${forId && canWrite ? ` for="${forId}"` : ''}>${escapeHtml(label)}</label>
            ${control}
          </div>`;
  const models = ['claude', 'codex', 'pi'].map((id) => {
    const key = agentModelField(id);
    const value = task[key] || '';
    const control = canWrite
      ? `<input type="text" id="task-detail-model-${id}" class="form-input form-input-sm task-detail-model-input" data-model-field="${key}" list="task-detail-model-${id}-list" maxlength="200" autocomplete="off" placeholder="${attr(t('taskField.modelPlaceholder'))}" value="${attr(value)}">
            <datalist id="task-detail-model-${id}-list">${MODEL_SUGGESTIONS[id].map(m => `<option value="${attr(m)}"></option>`).join('')}</datalist>`
      : staticField(value ? escapeHtml(value) : muted(t('taskField.modelPlaceholder')));
    return field(key, t('taskField.model'), control, { hidden: agent !== id, forId: `task-detail-model-${id}` });
  }).join('');
  const agentControl = canWrite
    ? `<select id="task-detail-agent" class="form-select form-select-sm task-detail-agent-select">
              ${['', ...AGENT_ASSIGNEES].map(a => `<option value="${a}"${a === (agent || '') ? ' selected' : ''}>${escapeHtml(taskAgentLabel(a))}</option>`).join('')}
            </select>`
    : staticField(escapeHtml(taskAgentLabel(agent)));
  const effortControl = canWrite
    ? `<select id="task-detail-effort" class="form-select form-select-sm task-detail-effort-select">
              ${['', ...EFFORT_LEVELS].map(e => `<option value="${e}"${e === (task.effort || '') ? ' selected' : ''}>${escapeHtml(effortLabel(e))}</option>`).join('')}
            </select>`
    : staticField(task.effort ? escapeHtml(effortLabel(task.effort)) : muted(effortLabel(null)));
  const designControl = canWrite
    ? `<label class="task-detail-toggle task-detail-design-field"><input type="checkbox" id="task-detail-design-mode" class="task-detail-design-toggle"${task.claude_design_mode ? ' checked' : ''}><span>${escapeHtml(t('taskField.designModeHint'))}</span></label>`
    : staticField(escapeHtml(t(task.claude_design_mode ? 'taskField.yes' : 'taskField.no')));
  return `
        <section class="task-detail-agent" data-section="agent" aria-labelledby="task-detail-agent-title">
          <div class="task-detail-agent-head">
            <span class="task-detail-field-label" id="task-detail-agent-title">${escapeHtml(t('taskField.agentSection'))}</span>
            <span class="task-detail-agent-hint">${escapeHtml(t('taskField.agentHint'))}</span>
          </div>
          <div class="task-detail-fields task-detail-agent-fields">
          ${field('agent_assignee', t('taskField.agent'), agentControl, { forId: 'task-detail-agent' })}
          ${models}
          ${field('effort', t('taskField.effort'), effortControl, { hidden: !agentHasEffort(agent), forId: 'task-detail-effort' })}
          ${field('claude_design_mode', t('taskField.designMode'), designControl, { hidden: !agentHasDesignMode(agent), forId: 'task-detail-design-mode' })}
          ${renderTaskPermissionMode(task, canWrite)}
          </div>
        </section>`;
}

// Re-derives the Agent run section from currentState.task in place after an agent save — which
// model input, Effort and Design mode show — and the Category select, which the API flips from
// Human to Coding when a Human task gets an AI agent. No repaint (that would discard a model id
// being typed); the controls already show what was picked.
function syncAgentFields() {
  if (!currentState || !currentState.root) return;
  const { root, task } = currentState;
  const agent = AGENT_ASSIGNEES.includes(task.agent_assignee) ? task.agent_assignee : null;
  for (const id of ['claude', 'codex', 'pi']) {
    const wrapper = root.querySelector(`.task-detail-agent [data-field="${agentModelField(id)}"]`);
    if (wrapper) wrapper.hidden = agent !== id;
  }
  const effort = root.querySelector('.task-detail-agent [data-field="effort"]');
  if (effort) effort.hidden = !agentHasEffort(agent);
  const design = root.querySelector('.task-detail-agent [data-field="claude_design_mode"]');
  if (design) design.hidden = !agentHasDesignMode(agent);
  const agentSelect = root.querySelector('.task-detail-agent-select');
  if (agentSelect && agentSelect !== document.activeElement) agentSelect.value = agent || '';
  const category = root.querySelector('.task-detail-category-select');
  if (category) category.value = task.category === 'HUMAN' ? 'HUMAN' : 'CODING';
}

// What renderRemoteSessionControls() needs besides the task and the view: the relay's own write
// verdict, once it has given one, overrides the project row this drawer loaded.
function remoteContext(state = currentState) {
  const user = getUser();
  return {
    language: state?.project?.language || 'en',
    userId: user?.id ?? null,
    canWrite: can(state?.project, user, 'task:write') && remoteView?.canWrite !== false,
    canStartTask: canStartTask(state?.task, user),
    closed: closedNames(state?.statuses).includes(state?.task?.status),
  };
}

// One session: its device, agent and state, then Attach and Stop. Stop asks first, in place: the
// terminal dialog is itself a modal, and openModal() replaces the open one.
function renderRemoteRow(row, task, language) {
  const text = (key, params) => escapeHtml(remoteText(language, key, params));
  const id = escapeHtml(row.id);
  const actions = [
    row.canAttach ? `<button type="button" class="btn btn-ghost btn-sm remote-session-attach" data-remote-focus="attach:${id}">${text('attach')}</button>` : '',
    row.canStop ? `<button type="button" class="btn btn-ghost btn-sm remote-session-stop" data-remote-focus="stop:${id}"${row.stopping ? ' disabled' : ''}>${text(row.stopping ? 'stopping' : 'stop')}</button>` : '',
  ].join('');
  return `<div class="remote-session-row" data-session-id="${id}">
          <span class="remote-session-dot remote-session-dot--${escapeHtml(row.state)}" aria-hidden="true"></span>
          <span class="remote-session-summary">
            <span class="remote-session-device">${escapeHtml(row.deviceName)}</span>
            <span class="remote-session-meta">${[escapeHtml(row.agent), text(row.stateKey)].filter(Boolean).join(' · ')}</span>
          </span>
          ${actions ? `<span class="remote-session-actions">${actions}</span>` : ''}
          ${row.confirming ? renderStopConfirm(row.id, task.task_key, row.deviceName, language) : ''}
        </div>`;
}

function renderStopConfirm(sessionId, taskKey, deviceName, language) {
  const text = (key, params) => escapeHtml(remoteText(language, key, params));
  const id = escapeHtml(sessionId);
  return `<div class="remote-session-confirm" role="group">
            <span class="remote-session-confirm-text">${text('stopPrompt', { task: taskKey, device: deviceName })}</span>
            <button type="button" class="btn btn-ghost btn-sm remote-session-stop-keep" data-remote-focus="keep:${id}">${text('stopKeep')}</button>
            <button type="button" class="btn btn-danger btn-sm remote-session-stop-confirm" data-remote-focus="confirm:${id}">${text('stopConfirm')}</button>
          </div>`;
}

function renderRemotePicker(picker, language) {
  const text = (key, params) => escapeHtml(remoteText(language, key, params));
  const off = picker.canStart ? '' : ' disabled';
  return `<div class="remote-session-picker">
          <label class="remote-session-field">
            <span class="remote-session-label">${text('device')}</span>
            <select class="form-select form-select-sm remote-session-device-select" data-remote-focus="device"${picker.devices.length > 1 ? '' : ' disabled'}>
              ${picker.devices.map(d => `<option value="${Number(d.id)}"${d.id === picker.deviceId ? ' selected' : ''}>${escapeHtml(d.name)}</option>`).join('')}
            </select>
          </label>
          <label class="remote-session-field">
            <span class="remote-session-label">${text('agent')}</span>
            <select class="form-select form-select-sm remote-session-agent-select" data-remote-focus="agent">
              <option value="">${text('taskDefaultAgent')}</option>
              ${picker.agents.map(a => `<option value="${escapeHtml(a.id)}"${a.id === picker.agent ? ' selected' : ''}>${escapeHtml(a.label)}</option>`).join('')}
            </select>
          </label>
          <button type="button" class="btn btn-primary btn-sm remote-session-start" data-remote-focus="start"${off}>${text(picker.starting ? 'starting' : 'start')}</button>
        </div>`;
}

const REMOTE_CONNECTION_NOTES = { connecting: 'connectingDevices', reconnecting: 'reconnectingDevices', auth: 'signInAgain', stopped: 'relayStopped' };

// The section under the task title: running sessions, else the device and agent picker with Start.
// Every value comes from a device or the relay, so all of it goes through escapeHtml() into text
// nodes; escapeHtml() leaves quotes alone, which is why no device name reaches an attribute.
export function renderRemoteSessionControls(task, view, ctx) {
  const model = remoteControlsModel(task, view, ctx);
  if (model.kind === 'hidden') return '';
  const text = (key, params) => escapeHtml(remoteText(ctx.language, key, params));
  const note = (key, params, button = '') => `<p class="remote-session-note">${text(key, params)}${button}</p>`;
  const action = (name, key, off = false) => ` <button type="button" class="btn btn-ghost btn-sm ${name}" data-remote-focus="${name}"${off ? ' disabled' : ''}>${text(key)}</button>`;
  let body = '';
  if (model.kind === 'checking') body = note('checking');
  else if (model.kind === 'failed') body = note('loadFailed', {}, action('remote-session-refresh', 'tryAgain'));
  else if (model.kind === 'revoked') body = note('accessLost');
  else if (model.kind === 'empty') {
    body = note('noDevices', {}, action('remote-session-refresh', 'checkAgain', model.checking)) + note('noDevicesHint');
  } else {
    const connectionNote = REMOTE_CONNECTION_NOTES[model.connection];
    body = model.rows.map(row => renderRemoteRow(row, task, ctx.language)).join('')
      + (model.picker ? renderRemotePicker(model.picker, ctx.language) : '')
      + (model.noteKey ? note(model.noteKey, model.noteParams) : '')
      + (connectionNote ? note(connectionNote, {}, model.connection === 'stopped' ? action('remote-session-retry', 'tryAgain') : '') : '')
      + (view.error ? `<p class="remote-session-note remote-session-note--error" role="alert">${escapeHtml(view.error)}</p>` : '');
  }
  return `<section class="remote-session" aria-label="${text('sectionTitle')}">
        <div class="remote-session-title">${text('sectionTitle')}</div>
        ${body}
      </section>`;
}

function renderPanelContent(task, members, children) {
  const canWrite = detailCan('task:write');
  const canComment = detailCan('comment:write');
  // Without task:write the fields render as plain values (`.task-detail-static`), not
  // disabled form controls — a viewer reads the task, nothing looks broken or locked.
  const myRole = effectiveRole(currentState.project, getUser());
  const roleName = myRole ? roleLabel(myRole) : '';
  const staticField = (inner) => `<div class="task-detail-static">${inner}</div>`;
  const assigneeMember = task.assignee ? members.find(m => m.user_id === task.assignee) || null : null;
  const statusAttrs = statusBadgeAttrs(currentState.statuses.find(s => s.name === task.status));
  const catClass = task.category === 'CODING' ? 'badge-coding' : 'badge-human';
  const deps = task.dependencies || [];
  const isPage = currentState.mode === 'page';
  const showSprint = sprintsEnabled(currentState.project); // C1334
  const sprintLabelSingular = groupLabelSingular(currentState.project && currentState.project.task_group_label); // C1395
  const detailHref = (k) => `#/projects/${currentState.projectId}/tasks/${encodeURIComponent(k)}`;

  // (C1389, widened C1559) "Start in Task App" — every task now gets this button; which
  // of the 3 branches fires (seed an origin-linked planning chat / start the first
  // startable child / start the task directly) is decided server-side by the Task App's
  // /start-task route, never here — the web app can't see the real child set reliably
  // (children below come from GET /tasks/:taskKey/children, degrade to the caller's rows or
  // [] when that read fails, and go stale between render and click). Gated on device linkage (lib/devices.js,
  // currentState.devices — fetched alongside statuses/project by both openTaskDetail()
  // and mountTaskDetail(), same discipline as § Sprint Dropdown / § Dynamic statuses
  // above). No localhost reachability probe — Safari mixed-content and Chrome Private
  // Network Access make that unreliable; the real reachability/account/project check
  // happens server-side once clicked (see tt-objective-chat.md § Web→Task App handoff).
  const startInTaskAppHtml = (() => {
    if (!canWrite) return '';
    const user = getUser();
    const linkedProjectIds = new Set(
      (currentState.devices || []).flatMap(d => d.project_ids || []).map(String)
    );
    const linked = user && linkedProjectIds.has(String(currentState.projectId));
    if (!linked) {
      return `<button class="btn btn-ghost btn-sm task-detail-start-in-app" disabled title="${escapeHtml('No linked Task App device found for this project — run Task App setup on that machine.')}">Start in Task App</button>`;
    }
    const url = buildStartTaskUrl({ projectId: currentState.projectId, taskKey: task.task_key, userId: user.id });
    return `<a class="btn btn-ghost btn-sm task-detail-start-in-app" href="${escapeHtml(url)}" target="_blank" rel="noopener">Start in Task App</a>`;
  })();

  // Find parent task from allTasks
  let parentTask = null;
  if (task.parent_id && currentState.options.allTasks) {
    parentTask = currentState.options.allTasks.find(t => t.id === task.parent_id);
  }
  if (!parentTask && currentState.options.parentTask?.id === task.parent_id) {
    parentTask = currentState.options.parentTask;
  }

  // ── Notifications tab badge (TPT18) ── unread = event id past this user's last-seen
  // marker AND not authored by this user (see lib/task-events-seen.js). Rows already
  // scrolled into view this session (observedEventIds, live-decremented by
  // observeUnreadEvents()) are excluded so the badge count matches what's still actually
  // unseen on screen, not just what's unseen-per-the-persisted-marker.
  const currentUser = getUser();
  const seenEventId = getSeenEventId(currentState.projectId, task.task_key);
  const unreadCount = unreadEventIds(taskEvents, seenEventId, currentUser && currentUser.id)
    .filter(id => !observedEventIds.has(id)).length;
  const notifBadgeHtml = unreadCount
    ? `<span class="task-detail-tab-badge">${unreadCount > 99 ? '99+' : unreadCount}</span>`
    : '';

  let html = `
    <div class="task-detail-header">
      <div class="task-detail-key-row">
        <span class="badge ${catClass}">${escapeHtml(task.task_key)}</span>
        ${getTypeIcon(task.type || 'task')}
        <span class="task-detail-type-label" style="color:${TYPE_COLORS[task.type] || TYPE_COLORS.task};font-size:0.8rem;font-weight:600">${TYPE_LABELS[task.type] || 'Task'}</span>
        ${canWrite ? '' : `<span class="view-only-pill" title="${escapeHtml(roleName ? `Your role (${roleName}) can view tasks in this project but not change them.` : 'You can view this task but not change it.')}"><svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>View only</span>`}
        ${currentState.mode === 'panel' ? '<button class="task-detail-close" title="Close">&times;</button>' : ''}
      </div>
      ${canWrite ? `<div class="task-detail-field" data-field="type">
        <label class="task-detail-field-label">Type</label>
        <select class="form-select form-select-sm task-detail-type-select">
          ${TYPES.map(t => `<option value="${t}" ${t === task.type ? 'selected' : ''}>${TYPE_LABELS[t]}</option>`).join('')}
        </select>
      </div>` : ''}
    </div>

    <div class="task-detail-body">
      <div class="task-detail-intro">
        ${renderParentContext(parentTask)}
        <h2 class="task-detail-title${canWrite ? '' : ' task-detail-readonly'}" data-field="title"${canWrite ? ' title="Click to edit"' : ''}>${escapeHtml(task.title)}</h2>
        ${startInTaskAppHtml ? `<div class="task-detail-start-in-app-row">${startInTaskAppHtml}</div>` : ''}
        <div class="remote-session-host">${renderRemoteSessionControls(task, remoteView, remoteContext())}</div>
      </div>

      <div class="task-detail-tabs" role="tablist" aria-label="Task detail views">
        <button class="task-detail-tab${activeTab === 'details' ? ' active' : ''}" type="button" role="tab" data-tab="details" aria-selected="${activeTab === 'details'}">Details</button>
        <button class="task-detail-tab${activeTab === 'comments' ? ' active' : ''}" type="button" role="tab" data-tab="comments" aria-selected="${activeTab === 'comments'}">Comments${comments.length ? ` (${comments.length})` : ''}</button>
        <button class="task-detail-tab${activeTab === 'notifications' ? ' active' : ''}" type="button" role="tab" data-tab="notifications" aria-selected="${activeTab === 'notifications'}">Notifications${notifBadgeHtml}</button>
      </div>

      <div class="task-detail-tab-content">
      <div class="task-detail-tab-panel" data-tab="details"${activeTab === 'details' ? '' : ' hidden'}>
        <div class="task-detail-fields">
          <div class="task-detail-field" data-field="status">
            <label class="task-detail-field-label">Status</label>
            ${canWrite ? `<select class="form-select form-select-sm task-detail-status-select">
              ${availableStatuses(statusNames(currentState.statuses), task, getUser(), roleStatus(currentState.statuses, 'in_progress')?.name).map(s => `<option value="${escapeHtml(s)}" ${s === task.status ? 'selected' : ''}>${escapeHtml(statusLabel(s))}</option>`).join('')}
            </select>` : staticField(`<span class="badge ${statusAttrs.className}" style="${statusAttrs.style}">${escapeHtml(statusLabel(task.status))}</span>`)}
          </div>
          ${showSprint ? `
          <div class="task-detail-field" data-field="priority">
            <label class="task-detail-field-label">${escapeHtml(sprintLabelSingular)}</label>
            ${canWrite ? `<select class="form-select form-select-sm task-detail-priority-select">
              ${getSprintOptions(task.priority, currentState.options.sprints, sprintRowsOf(currentState), sprintLabelSingular)}
            </select>` : staticField(task.priority > 0 ? `${escapeHtml(sprintLabelSingular)} ${Number(task.priority)}` : '<span class="text-muted">Backlog</span>')}
          </div>` : ''}
          <div class="task-detail-field" data-field="assignee">
            <label class="task-detail-field-label">Member Assignee</label>
            ${canWrite ? `<div class="task-detail-assignee-combo">
              <span class="task-detail-assignee-avatar">${renderAvatarHtml(assigneeMember, 24)}</span>
              ${renderMemberCombo({ id: 'task-detail-assignee', assignee: task.assignee, members })}
            </div>` : staticField(assigneeMember
              ? `<span class="task-detail-assignee-avatar">${renderAvatarHtml(assigneeMember, 24)}</span><span>${escapeHtml(assigneeMember.name || assigneeMember.email || '')}</span>`
              : '<span class="text-muted">Unassigned</span>')}
          </div>
          <div class="task-detail-field" data-field="story_points">
            <label class="task-detail-field-label">Story Points</label>
            ${canWrite
              ? `<input type="number" class="form-input form-input-sm task-detail-sp-input" min="0" value="${task.story_points != null ? task.story_points : ''}" placeholder="—">`
              : staticField(task.story_points != null ? escapeHtml(String(task.story_points)) : '<span class="text-muted">—</span>')}
          </div>
          <div class="task-detail-field" data-field="urgency">
            <label class="task-detail-field-label" for="task-detail-urgency">${urgencyCopy(currentState.project?.language).field}</label>
            ${canWrite ? `<select id="task-detail-urgency" class="form-select form-select-sm task-detail-urgency-select">
              ${URGENCY_VALUES.map(n => `<option value="${n}"${n === (task.urgency ?? 0) ? ' selected' : ''}>${urgencyLabel(n, currentState.project?.language)}</option>`).join('')}
            </select>` : staticField(task.urgency ? renderUrgencyBadge(task, currentState.project?.language, escapeHtml) : urgencyLabel(0, currentState.project?.language))}
          </div>
          ${renderDueField(task, canWrite, closedNames(currentState.statuses))}
          <div class="task-detail-field" data-field="category">
            <label class="task-detail-field-label" for="task-detail-category">${escapeHtml(t('taskField.category'))}</label>
            ${canWrite ? `<select id="task-detail-category" class="form-select form-select-sm task-detail-category-select">
              ${['CODING', 'HUMAN'].map(c => `<option value="${c}"${c === (task.category || 'CODING') ? ' selected' : ''}>${escapeHtml(t(`category.${c}`))}</option>`).join('')}
            </select>` : staticField(escapeHtml(t(`category.${task.category === 'HUMAN' ? 'HUMAN' : 'CODING'}`)))}
          </div>
          <div class="task-detail-field" data-device-field${task.active_device || task.recent_device ? '' : ' hidden'}><span class="task-detail-field-label">${deviceFieldLabel(currentState.project?.language)}</span>${renderDeviceAttribution(task, currentState.project?.language)}</div>
        </div>

        ${renderAgentSection(task, canWrite)}

        <div class="task-detail-field task-detail-section" data-field="description">
          <label class="task-detail-field-label">Description</label>
          <div class="task-detail-description md-preview${canWrite ? '' : ' task-detail-readonly'}"${canWrite ? ' title="Click to edit"' : ''}>${descriptionHtml(task.description, members)}</div>
        </div>

        <div class="task-detail-field task-detail-section" data-field="tags">
          <label class="task-detail-field-label">Tags</label>
          ${canWrite ? '<input type="text" class="task-detail-tag-input" placeholder="Add tag...">' : `<div class="task-detail-tags">${(task.tags || []).map(tag => `<span class="tag">${escapeHtml(tag)}</span>`).join('') || '<span class="text-muted">None</span>'}</div>`}
        </div>

        ${renderDevLinks(task.dev_links, currentState.project?.language, canWrite)}

        <div class="task-detail-field task-detail-section" data-field="dependencies">
          <label class="task-detail-field-label"${canWrite ? ' for="task-detail-dep-input"' : ''}>${escapeHtml(t('taskField.dependencies'))}</label>
          ${canWrite
            ? `<input type="text" id="task-detail-dep-input" class="task-detail-dep-input" placeholder="${escapeHtml(t('deps.placeholder'))}">`
            : `<div class="task-detail-deps">${renderDependencyChips(deps, depInfoMap(currentState), { statusText: statusLabel })}</div>`}
          <p class="task-detail-deps-message" role="status" hidden></p>
          ${canWrite ? `<div class="task-detail-deps-floor" hidden>
            <span class="task-detail-deps-floor-text"></span>
            <button type="button" class="btn btn-ghost btn-sm task-detail-deps-move"></button>
          </div>` : ''}
        </div>

        <div class="task-detail-field task-detail-section" data-field="parent_id">
          <label class="task-detail-field-label">Parent</label>
          <div class="task-detail-parent">
            ${parentTask
              ? (isPage
                  ? `<a class="detail-parent-link" href="${detailHref(parentTask.task_key)}">${getTypeIcon(parentTask.type || 'task')} ${escapeHtml(parentTask.task_key)} — ${escapeHtml(parentTask.title)}</a>`
                  : `<span class="detail-parent-link" data-task-key="${escapeHtml(parentTask.task_key)}">${getTypeIcon(parentTask.type || 'task')} ${escapeHtml(parentTask.task_key)} — ${escapeHtml(parentTask.title)}</span>`)
                + (canWrite ? '<button class="btn btn-ghost btn-sm task-detail-remove-parent">Remove</button>' : '')
              : `<span class="text-muted">None</span>`}
            ${canWrite ? '<button class="btn btn-ghost btn-sm task-detail-change-parent">Change Parent</button>' : ''}
          </div>
        </div>

        <div class="task-detail-field task-detail-section" data-field="is_objective">
          <span class="task-detail-field-label">${escapeHtml(t('taskField.objective'))}</span>
          ${canWrite
            ? `<label class="task-detail-toggle"><input type="checkbox" class="task-detail-objective-toggle"${task.is_objective ? ' checked' : ''}><span>${escapeHtml(t('taskField.objectiveHint'))}</span></label>`
            : `<div class="task-detail-static">${escapeHtml(t(task.is_objective ? 'taskField.yes' : 'taskField.no'))}</div>`}
        </div>

        <div class="task-detail-field task-detail-section detail-children">
          <label class="task-detail-field-label">Children ${children.length ? `(${children.length})` : ''}</label>
          ${children.length ? `
          <div class="task-detail-children-list">
            ${children.map(c => `
            <${isPage ? 'a' : 'div'} class="task-detail-child"${isPage ? ` href="${detailHref(c.task_key)}"` : ` data-task-key="${escapeHtml(c.task_key)}"`}>
              ${getTypeIcon(c.type || 'task')}
              <span class="badge badge-sm">${escapeHtml(c.task_key)}</span>
              <span class="task-detail-child-title">${escapeHtml(c.title)}</span>
              <span class="badge badge-sm ${statusBadgeAttrs(currentState.statuses.find(s => s.name === c.status)).className}" style="${statusBadgeAttrs(currentState.statuses.find(s => s.name === c.status)).style}">${escapeHtml(statusLabel(c.status))}</span>
              ${c.story_points != null ? `<span class="text-muted text-sm">${c.story_points}sp</span>` : ''}
            </${isPage ? 'a' : 'div'}>`).join('')}
          </div>` : (task.is_objective
            ? '<div class="text-muted">Subtasks come from the Objective chat in the Task App.</div>'
            : '<div class="text-muted">No children</div>')}
          ${!canWrite || task.is_objective ? '' : '<button class="btn btn-ghost btn-sm task-detail-add-child mt-05">+ Add Child</button>'}
        </div>
      </div>

      <div class="task-detail-tab-panel" data-tab="comments"${activeTab === 'comments' ? '' : ' hidden'}>
        <div class="task-detail-field task-detail-comments">
          <div class="task-detail-comments-list">
            ${comments.length
              ? comments.map(c => renderCommentRow(c, members)).join('')
              : '<div class="text-muted">No comments yet</div>'}
          </div>
          ${canComment ? `<textarea class="form-textarea task-detail-comment-input" rows="3" placeholder="Add a comment..."></textarea>
          <div class="comment-post-row">
            <button class="btn btn-primary btn-sm task-detail-comment-submit">Post</button>
          </div>` : `<p class="task-detail-readonly-note">${escapeHtml(roleName ? `Your role (${roleName}) can read comments but not post them.` : 'You can read comments but not post them.')}</p>`}
        </div>
      </div>

      <div class="task-detail-tab-panel" data-tab="notifications"${activeTab === 'notifications' ? '' : ' hidden'}>
        <div class="task-detail-subscribe">
          <label><input type="checkbox" class="task-detail-subscribe-toggle" ${eventsSubscribed ? 'checked' : ''}> Subscribed</label>
          <button class="btn btn-ghost btn-sm task-detail-events-refresh" type="button">Refresh</button>
        </div>
        <div class="task-detail-events-list">
          ${taskEvents.length
            ? taskEvents.map(e => `
              <div class="task-detail-event" data-event-id="${e.id}">
                <div class="task-detail-event-meta">
                  <span class="task-detail-event-type">${escapeHtml(EVENT_TYPE_LABELS[e.event_type] || e.event_type)}</span>
                  <strong>${escapeHtml((e.actor && (e.actor.name || e.actor.email)) || 'System')}</strong>
                  <span class="text-muted text-sm">${formatDate(e.created_at)}</span>
                </div>
                <div class="task-detail-event-body">
                  ${escapeHtml(e.title || '')}
                  ${e.body ? `<div class="text-muted text-sm">${escapeHtml(e.body)}</div>` : ''}
                </div>
              </div>`).join('')
            : '<div class="text-muted">No activity yet</div>'}
        </div>
      </div>
      </div>
    </div>

    <div class="task-detail-footer">
      <div class="task-detail-timestamps">
        <span class="text-muted text-sm">Created ${formatDate(task.created_at)}</span>
        <span class="text-muted text-sm">Updated ${formatDate(task.updated_at)}</span>
      </div>
      ${detailCan('task:delete') ? '<button class="btn btn-danger btn-sm task-detail-delete-btn">Delete</button>' : ''}
    </div>`;

  return html;
}

// Test seam: renders the panel markup for a given state without mounting anything, so a
// node:test can assert what a role sees. Restores the module state it borrows.
export function renderTaskDetailHtml(state, children = []) {
  const previous = currentState;
  currentState = state;
  try {
    return renderPanelContent(state.task, state.members || [], children);
  } finally {
    currentState = previous;
  }
}

// Merges a PATCH reply into the caller's row of the same key. The reply is a full row with no
// `description_truncated` key, so a capped `fields=board` row would otherwise keep its stale `true`
// and a board repaint would mark the now-full description as cut.
export function syncSavedRow(rows, taskKey, task) {
  const idx = rows.findIndex(t => t.task_key === taskKey);
  if (idx >= 0) Object.assign(rows[idx], task, { description_truncated: false });
}

// Saves one field of the task `state` shows (the open task by default) and resolves whether it
// landed. The reply goes into that state's own row even when the drawer closed or moved to another
// task meanwhile, so a late reply never lands on the wrong task; the field feedback and the Due
// date sync wait for `state` to still be on screen. A failure is always toasted.
async function patchField(field, value, explicitFieldEl = null, state = currentState) {
  if (!state || !can(state.project, getUser(), 'task:write')) return false;
  const { projectId, taskKey } = state;
  const fieldEl = explicitFieldEl || state.root.querySelector(`.task-detail-${field}-select, .task-detail-sp-input, .task-detail-title, .task-detail-description`);

  try {
    const { task } = await api.patch('/projects/' + projectId + '/tasks/' + taskKey, { [field]: value });
    // Write replies omit attribution; retain the latest lifecycle state, including events
    // received while the save was in flight.
    task.active_device = state.task.active_device ?? null;
    task.recent_device = state.task.recent_device ?? null;
    task.dev_links = state.task.dev_links || [];
    state.task = task;
    if (field === 'urgency') syncUrgencyBadges(task, state.project?.language);

    // Sync back to allTasks
    const allTasks = state.options.allTasks;
    if (allTasks) syncSavedRow(allTasks, taskKey, task);

    if (currentState === state) {
      showFieldFeedback(fieldEl, true);
      // Any save can move the Due date pill — a status change into or out of a closed role too.
      syncDueField();
    }
    return true;
  } catch (err) {
    state.lastSaveError = err.message || 'Failed to save';
    showToast(err.message || 'Failed to save', 'error');
    if (currentState === state) showFieldFeedback(fieldEl, false);
    return false;
  }
}

// Re-derives the Due date field's clear button and state pill from currentState.task, in place
// (TPT589). Never repaints — that would discard a date the user is still typing — and never
// touches the input itself (queueDueSave() decides when the input shows the saved date).
function syncDueField() {
  if (!currentState || !currentState.root) return;
  const field = currentState.root.querySelector('.task-detail-due-field');
  if (!field) return;
  const clear = field.querySelector('.task-detail-due-clear');
  if (clear) clear.hidden = !currentState.task.due_date;
  const pill = field.querySelector('.task-detail-due-state');
  if (pill) {
    const due = taskDueState(currentState.task, closedNames(currentState.statuses));
    pill.hidden = !due;
    pill.textContent = due ? due.label : '';
    pill.classList.toggle('overdue', !!(due && due.overdue));
  }
}

// Re-derives the Permission mode hint from currentState.task in place after a save (TPT9063) —
// no repaint, same discipline as syncDueField(); the select already shows what was picked.
function syncPermissionModeHint() {
  if (!currentState || !currentState.root) return;
  const hint = currentState.root.querySelector('.task-detail-permission-hint');
  if (!hint) return;
  hint.textContent = effectivePermissionModeHint(
    currentState.task.agent_permission_mode ?? null,
    currentState.project?.agent_permission_mode ?? null,
  );
}

// Re-derives the Cowboy-coding mode card from currentState in place after a Permission mode or
// cowboy_mode save (TPT9164), same discipline as syncPermissionModeHint(): shown only while
// isCowboyAllowed(), checked only while shown and the saved flag is on. Leaving bypass therefore
// hides and unchecks it, matching the false the API stored with the new mode.
function syncCowboyField() {
  if (!currentState || !currentState.root) return;
  const card = currentState.root.querySelector('.task-detail-cowboy');
  const toggle = card && card.querySelector('.task-detail-cowboy-toggle');
  if (!toggle) return;
  const allowed = isCowboyAllowed(currentState.task.agent_permission_mode ?? null, currentState.project?.agent_permission_mode ?? null);
  card.hidden = !allowed;
  toggle.value = allowed ? normalizeCowboyScope(currentState.task.cowboy_mode) : 'disabled';
}

// A date input's value as a day to save: null to clear, `YYYY-MM-DD`, or undefined while the date
// is still being typed (a partly filled field reports badInput, a year being typed reads `0002-…`).
function dueInputValue(input) {
  return input.validity && input.validity.badInput ? undefined : parseDayInput(input.value);
}

// Queues a save of `input`'s date for the task `state` shows (TPT589). A queued job reads the input
// when it runs, so the newest value wins and the jobs behind it dedupe to nothing. A job is never
// dropped because the drawer closed or moved on — it saves through `state`, so the last date typed
// still lands; only the DOM follow-up needs `state` on screen.
function queueDueSave(input, state = currentState) {
  if (!state || !input) return;
  duePending++;
  dueSaveChain = dueSaveChain
    .then(() => saveDueDate(input, state))
    .catch(() => {})
    .then(() => {
      duePending--;
      // A repaint while a save was in flight rendered the old date into a fresh input: once the
      // queue is idle, show the saved date there unless the user is in it.
      if (duePending > 0 || currentState !== state || !state.root) return;
      const live = state.root.querySelector('.task-detail-due-input');
      if (live && live !== document.activeElement) live.value = state.task.due_date || '';
    });
}

async function saveDueDate(input, state) {
  const value = dueInputValue(input);
  if (value === undefined || value === (state.task.due_date || null)) return;
  const ok = await patchField('due_date', value, input, state);
  // A failed save shows the saved date again — unless the input has moved on to a newer value.
  if (!ok && input.value === (value || '')) input.value = state.task.due_date || '';
}

function showFieldFeedback(el, success) {
  if (!el) return;
  const wrapper = el.closest('.task-detail-field') || el;
  if (success) {
    const check = document.createElement('span');
    check.className = 'task-detail-saved';
    check.textContent = ' \u2713';
    wrapper.appendChild(check);
    setTimeout(() => check.remove(), 800);
  } else {
    wrapper.classList.add('task-detail-field-error');
    setTimeout(() => wrapper.classList.remove('task-detail-field-error'), 400);
  }
}

function startTitleEdit() {
  if (!detailCan('task:write')) return;
  if (!currentState) return;
  const titleEl = currentState.root.querySelector('.task-detail-title');
  if (!titleEl) return;

  titleEl.contentEditable = 'true';
  titleEl.textContent = currentState.task.title;
  titleEl.classList.add('editing');
  titleEl.focus();

  const range = document.createRange();
  range.selectNodeContents(titleEl);
  window.getSelection().removeAllRanges();
  window.getSelection().addRange(range);

  function save() {
    titleEl.contentEditable = 'false';
    titleEl.classList.remove('editing');
    const newTitle = titleEl.textContent.trim();
    if (newTitle && newTitle !== currentState.task.title) {
      patchField('title', newTitle);
    } else {
      titleEl.textContent = currentState.task.title;
    }
  }

  titleEl.addEventListener('blur', save, { once: true });
  titleEl.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') { e.preventDefault(); titleEl.blur(); }
    if (e.key === 'Escape') { titleEl.textContent = currentState.task.title; titleEl.blur(); }
  });
}

// caretOffsetFromClick(evt, rawText, sourceEl) — hit-tests a click inside the rendered
// markdown (`sourceEl`, still in the DOM) and maps it to an approximate offset in
// `rawText` via caret-map.js's rawOffsetFromRendered(). Must run BEFORE sourceEl is
// replaced — caretRangeFromPoint/caretPositionFromPoint only work against live layout.
// Returns null on any miss (no browser API, click landed on a non-text node such as an
// <img> or padding, hit node outside sourceEl, or no probe matched) — callers treat null
// as "fall back to end-of-text", never throw.
function caretOffsetFromClick(evt, rawText, sourceEl) {
  if (!evt || !sourceEl || typeof evt.clientX !== 'number') return null;
  try {
    let node = null;
    let offset = 0;
    if (typeof document.caretRangeFromPoint === 'function') {
      const range = document.caretRangeFromPoint(evt.clientX, evt.clientY);
      if (!range) return null;
      node = range.startContainer;
      offset = range.startOffset;
    } else if (typeof document.caretPositionFromPoint === 'function') {
      const pos = document.caretPositionFromPoint(evt.clientX, evt.clientY);
      if (!pos) return null;
      node = pos.offsetNode;
      offset = pos.offset;
    } else {
      return null; // neither API available (unsupported browser)
    }

    if (!node || node.nodeType !== Node.TEXT_NODE || !sourceEl.contains(node)) return null;

    // Walk sourceEl's text nodes to build the flattened rendered text + the absolute
    // index of the click within it (renderMarkdown() output has no other text source).
    const walker = document.createTreeWalker(sourceEl, NodeFilter.SHOW_TEXT);
    let renderedText = '';
    let renderedIndex = null;
    let n;
    while ((n = walker.nextNode())) {
      if (n === node) renderedIndex = renderedText.length + offset;
      renderedText += n.nodeValue;
    }
    if (renderedIndex == null) return null;

    return rawOffsetFromRendered(renderedText, renderedIndex, rawText);
  } catch {
    return null; // never let a caret hit-test miss break edit-mode entry
  }
}

// placeCaretFromClick(textarea, offset) — focuses `textarea` and sets its caret to
// `offset`, falling back to end-of-text when offset is null (no click evt, or
// caretOffsetFromClick() couldn't resolve one).
function placeCaretFromClick(textarea, offset) {
  textarea.focus();
  const pos = offset == null ? textarea.value.length : offset;
  textarea.setSelectionRange(pos, pos);
}

function startDescriptionEdit(evt) {
  if (!detailCan('task:write')) return;
  if (!currentState) return;
  const descEl = currentState.root.querySelector('.task-detail-description');
  if (!descEl) return;

  // Measure + hit-test BEFORE descEl is replaced (C1469) — both need the live element.
  const rect = descEl.getBoundingClientRect();
  const rawText = currentState.task.description || '';
  const caretOffset = evt ? caretOffsetFromClick(evt, rawText, descEl) : null;

  const textarea = document.createElement('textarea');
  textarea.className = 'form-textarea task-detail-desc-edit';
  textarea.value = rawText;
  textarea.rows = 8;
  // Copy the rendered block's height so swapping to the textarea causes no panel jump.
  // 120 = the CSS min-height floor (styles.css .task-detail-desc-edit) — never go below
  // it even for a one-line description, so the editor stays comfortably usable.
  textarea.style.height = Math.max(Math.ceil(rect.height), 120) + 'px';
  descEl.replaceWith(textarea);
  // Grow-to-fit: rendered markdown can be SHORTER than its raw source (e.g. a long link
  // renders as short link text), so the copied height may still clip content on entry.
  // Only grow, never shrink — shrinking here would reintroduce the jump this is fixing.
  if (textarea.scrollHeight > textarea.clientHeight) textarea.style.height = textarea.scrollHeight + 'px';
  placeCaretFromClick(textarea, caretOffset);
  // allowFiles:true (C1447) — this field also gets the Embed ▾ picker below, so
  // dropping/pasting a non-image file must work too, not just images.
  attachImageHandlers(textarea, currentState.projectId, currentState.taskKey, { allowFiles: true });

  // @mention typeahead (TPT33). Attached BEFORE the field's own Escape keydown
  // listener below (load-bearing — see mention-typeahead.js's onKeydown comment):
  // when its dropdown is open, Escape calls stopImmediatePropagation(), which only
  // suppresses a SAME-element listener registered after this one. `mentionPicking`
  // mirrors `pickerOpen` just below for the same reason: this field's blur is
  // destructive ({once:true} -> save() -> replaceWith), and a mousedown-commit inside
  // the mention dropdown must not let that blur fire mid-pick. Scoped to the
  // synchronous commit only (unlike `pickerOpen`'s async file-dialog window) — guarding
  // "dropdown open" instead would eat a genuine click-away's one blur and never save.
  let mentionPicking = false;
  const mentionTypeahead = attachMentionTypeahead(textarea, {
    getMembers: () => (currentState ? currentState.members : []),
    scrollEl: detailScrollEl(currentState),
    onPickStart: () => { mentionPicking = true; },
    onPickEnd: () => { mentionPicking = false; textarea.focus(); },
  });

  // Embed ▾ button + live thumbnail strip (C1447). `pickerOpen` guards against the
  // {once:true} blur below firing save() (which destroys `textarea` via replaceWith)
  // while the native file dialog is open — some engines fire element blur when the OS
  // dialog takes the window focus, and losing the textarea mid-upload would silently
  // drop the ref insertAtCursor was about to write.
  let pickerOpen = false;
  const embedControls = attachEmbedControls(textarea, {
    projectId: currentState.projectId,
    taskKey: currentState.taskKey,
    anchor: textarea,
    scrollEl: detailScrollEl(currentState),
    onPickerOpen: () => { pickerOpen = true; },
    onPickerDone: () => { pickerOpen = false; textarea.focus(); },
  });

  function save() {
    mentionTypeahead.destroy(); // before replaceWith — same discipline as embedControls below
    embedControls.destroy(); // before replaceWith — its block/listeners must die with the editor

    const newDesc = textarea.value.trim();
    const div = document.createElement('div');
    div.className = 'task-detail-description md-preview';
    div.title = 'Click to edit';

    if (newDesc !== (currentState.task.description || '')) {
      div.innerHTML = descriptionHtml(newDesc, currentState.members);
      textarea.replaceWith(div);
      patchField('description', newDesc);
    } else {
      div.innerHTML = descriptionHtml(currentState.task.description, currentState.members);
      textarea.replaceWith(div);
    }
  }

  function onBlur() {
    if (pickerOpen || mentionPicking) { textarea.addEventListener('blur', onBlur, { once: true }); return; }
    save();
  }
  textarea.addEventListener('blur', onBlur, { once: true });
  textarea.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      textarea.value = currentState.task.description || '';
      textarea.blur();
    }
  });
}

// mountTagTypeahead() — (re)wires the Tags field's chip-input/typeahead after any paint
// that replaces the rendered pane content. Sibling of mountAssigneeCombo() just above,
// same reason: renderPanelContent() runs on every parent change / comment post (via
// refreshPanelContent), so a tag change never triggers that full re-render itself (see
// onChange below) — only every OTHER repaint needs this re-mount. allowCreate:true keeps
// the register-a-new-tag-with-a-required-description flow this field has always had
// (C1038), now living in the shared lib/tag-typeahead.js instead of a bespoke block here.
function mountTagTypeahead() {
  if (!currentState || !currentState.root) return;
  const inputEl = currentState.root.querySelector('.task-detail-tag-input');
  if (tagTypeahead) { tagTypeahead.destroy(); tagTypeahead = null; }
  if (!inputEl) return;

  tagTypeahead = attachTagTypeahead(inputEl, {
    projectId: currentState.projectId,
    initialTags: currentState.task.tags || [],
    allowCreate: true,
    isAlive: () => document.body.contains(inputEl),
    scrollEl: detailScrollEl(currentState),
    onChange: (names) => {
      if (!currentState) return;
      currentState.task.tags = names;
      patchField('tags', names, inputEl);
    },
  });
}

// ── Depends on (TPT9213) ──
// The drawer edits task.dependencies with the New Task modal's chip typeahead
// (components/dependency-typeahead.js): server search, add, remove — never the task itself.
// Every change is checked first (GET /tasks/dependency-check, lib/task-fields.js) and saved only
// when nothing the user added is invalid: an unknown task, the task itself, a task that already
// depends on this one, or one of its parents. A rejected chip stays on screen, marked, with the
// reason under the field, until the user removes it; the other chips save as soon as it is gone.
// Dependencies already saved are reported but never block. The state lives on the drawer's own
// currentState — `depDraft` (the chips while they differ from the saved list), `depInfo` (check
// results), `depMessage` — so a repaint restores it and a late reply never reaches another task.

const DEP_STALE = Symbol('stale');
const upperKey = key => String(key).toUpperCase();

function sameKeySet(a, b) {
  const x = new Set((a || []).map(upperKey));
  const y = new Set((b || []).map(upperKey));
  return x.size === y.size && [...x].every(k => y.has(k));
}

// Check results (and picked rows) for `state`'s dependencies, keyed by upper-cased task key.
function depInfoMap(state) {
  return new Map((state?.depInfo || []).map(r => [upperKey(r.task_key), r]));
}

function mergeDepInfo(state, results) {
  const map = depInfoMap(state);
  for (const r of results || []) if (r && r.task_key) map.set(upperKey(r.task_key), r);
  state.depInfo = [...map.values()];
}

// (Re)wires the chip editor after any paint that replaced the pane, then repaints its notes.
function mountDependencyTypeahead() {
  if (!currentState || !currentState.root) return;
  if (depTypeahead) { depTypeahead.destroy(); depTypeahead = null; }
  const owner = currentState;
  const inputEl = owner.root.querySelector('.task-detail-dep-input');
  if (inputEl) {
    const closed = new Set(closedNames(owner.statuses));
    depTypeahead = attachDependencyTypeahead(inputEl, {
      projectId: owner.projectId,
      initialKeys: owner.depDraft || owner.task.dependencies || [],
      initialInfo: owner.depInfo || [],
      excludeKeys: [owner.task.task_key],
      statusText: statusLabel,
      isClosed: name => closed.has(name),
      scrollEl: detailScrollEl(owner),
      isAlive: () => currentState === owner,
      onChange: () => { syncDependencies(owner); },
    });
  }
  paintDependencyNotes(owner);
}

// Repaints what sits under Depends on from `state` alone — the message (with Try again after a
// failed request), a viewer's chips, and the sprint note. No request.
function paintDependencyNotes(state) {
  if (currentState !== state || !state.root) return;
  const field = state.root.querySelector('[data-field="dependencies"]');
  if (!field) return;
  const box = field.querySelector('.task-detail-deps-message');
  if (box) {
    const note = state.depMessage;
    box.hidden = !note;
    box.className = 'task-detail-deps-message' + (note ? ` task-detail-deps-message--${note.kind}` : '');
    box.innerHTML = note
      ? escapeHtml(note.text) + (note.retry ? ` <button type="button" class="btn btn-ghost btn-sm task-detail-deps-retry">${escapeHtml(t('deps.retry'))}</button>` : '')
      : '';
  }
  const chips = field.querySelector('.task-detail-deps');
  if (chips) chips.innerHTML = renderDependencyChips(state.task.dependencies || [], depInfoMap(state), { statusText: statusLabel });
  syncDependencyFloor(state);
}

// The sprint note under Depends on: an open dependency sits in this task's sprint or a later one,
// so this task would wait for work scheduled after it. Offers the move to the sprint after it —
// a writer's own decision, since a move can push this task's dependents too.
function syncDependencyFloor(state) {
  if (currentState !== state || !state.root) return;
  const box = state.root.querySelector('.task-detail-deps-floor');
  if (!box) return;
  const info = depInfoMap(state);
  const floor = dependencyFloor((state.task.dependencies || []).map(k => info.get(upperKey(k))).filter(Boolean));
  const current = Number(state.task.priority) || 0;
  const open = !closedNames(state.statuses).includes(state.task.status);
  const show = !!floor && open && current > 0 && current < floor.floor && sprintsEnabled(state.project);
  box.hidden = !show;
  if (!show) return;
  const group = groupLabelSingular(state.project && state.project.task_group_label);
  box.querySelector('.task-detail-deps-floor-text').textContent = t('deps.floorEdit', { key: floor.key, group, priority: floor.priority, current });
  const move = box.querySelector('.task-detail-deps-move');
  move.textContent = t('deps.floorMove', { group, floor: floor.floor });
  move.dataset.floor = String(floor.floor);
}

// Checks `keys` as `state`'s dependencies. Resolves the results; DEP_STALE when a newer check
// superseded this one; null when the request failed (then, unless `quiet`, state.depMessage
// says so with a Try again button). `lookupOnly` resolves the keys without the task's key: the
// rows and not_found only, skipping the project-wide loop and parent walk a change needs.
async function checkDependencyKeys(state, keys, { quiet = false, lookupOnly = false } = {}) {
  state.depSeq = (state.depSeq || 0) + 1;
  const mySeq = state.depSeq;
  let reply;
  try {
    reply = await api.get(dependencyCheckPath(state.projectId, keys, { taskKey: lookupOnly ? null : state.task.task_key }));
  } catch (err) {
    if (mySeq !== state.depSeq) return DEP_STALE;
    if (!quiet) state.depMessage = { kind: 'error', text: t('deps.checkFailed', { error: err.message || 'Request failed' }), retry: true };
    return null;
  }
  if (mySeq !== state.depSeq) return DEP_STALE;
  const results = (reply && reply.dependencies) || [];
  mergeDepInfo(state, results);
  if (currentState === state && depTypeahead) depTypeahead.setInfo(results);
  return results;
}

// Problems on dependencies that are already saved: reported, never blocking.
function savedDependencyWarning(state) {
  const info = depInfoMap(state);
  const problems = (state.task.dependencies || []).map(k => info.get(upperKey(k))).filter(r => r && r.problem);
  return problems.length ? { kind: 'warning', text: problems.map(dependencyProblemText).join(' ') } : null;
}

// Validates the chips the editor shows and saves them once nothing the user added is invalid.
async function syncDependencies(state) {
  if (!can(state.project, getUser(), 'task:write')) return;
  const keys = currentState === state && depTypeahead ? depTypeahead.getKeys() : (state.depDraft || []);
  const saved = state.task.dependencies || [];
  state.depDraft = sameKeySet(keys, saved) ? null : keys;
  const added = keys.filter(k => !saved.some(s => upperKey(s) === upperKey(k)));
  if (added.length) {
    const results = await checkDependencyKeys(state, keys);
    if (results === DEP_STALE) return;
    if (results === null) { paintDependencyNotes(state); return; }
  } else {
    state.depSeq = (state.depSeq || 0) + 1; // a check still in flight answers for chips no longer shown
  }
  const info = depInfoMap(state);
  const blocking = blockingDependencyResults(keys.map(k => info.get(upperKey(k))).filter(Boolean), saved);
  if (blocking.length) {
    state.depMessage = { kind: 'error', text: [...blocking.map(dependencyProblemText), t('deps.blockedEdit')].join(' ') };
    paintDependencyNotes(state);
    return;
  }
  if (!state.depDraft) {
    state.depMessage = savedDependencyWarning(state);
    paintDependencyNotes(state);
    return;
  }
  queueDependencySave(state);
}

// Saves `state`'s chips behind any save still running. A job reads the chips when it runs, so the
// newest list wins and a job behind it finds nothing left to save. Like the due date's queue, a job
// is never dropped because the drawer moved on: it saves through `state`.
function queueDependencySave(state) {
  depSaveChain = depSaveChain.then(async () => {
    const keys = state.depDraft;
    if (!keys) return;
    const field = currentState === state && state.root ? state.root.querySelector('[data-field="dependencies"]') : null;
    const ok = await patchField('dependencies', keys, field, state);
    if (ok) {
      if (sameKeySet(state.depDraft || [], state.task.dependencies || [])) state.depDraft = null;
      if (!state.depDraft) state.depMessage = savedDependencyWarning(state);
    } else {
      state.depMessage = { kind: 'error', text: t('deps.saveFailed', { error: state.lastSaveError || 'Request failed' }), retry: true };
    }
    paintDependencyNotes(state);
  }).catch(() => {});
}

// The first look at a freshly opened task's dependencies: their titles, status and sprints for
// the chips and the sprint note, and a saved dependency whose task is gone. A key lookup only —
// opening a task never pays for the loop search; a change runs the full check. Quiet on failure:
// the chips just show their keys.
function loadDependencyInfo(state) {
  const keys = state.task.dependencies || [];
  if (!keys.length) return;
  checkDependencyKeys(state, keys, { quiet: true, lookupOnly: true }).then((results) => {
    if (results === DEP_STALE || !results) return;
    if (can(state.project, getUser(), 'task:write') && !state.depDraft) state.depMessage = savedDependencyWarning(state);
    paintDependencyNotes(state);
  });
}

// Repaints only the Children section from currentState — its + Add Child button and empty-list
// text follow is_objective.
function repaintChildren(state) {
  if (currentState !== state || !state.root) return;
  const fresh = document.createElement('div');
  fresh.innerHTML = renderPanelContent(state.task, state.members, state.children || []);
  const next = fresh.querySelector('.detail-children');
  const prev = state.root.querySelector('.detail-children');
  if (next && prev) prev.replaceWith(next);
}

// mountMentionTypeaheads() (TPT33) — (re)wires the @mention typeahead on the comment
// compose box. Sibling of mountAssigneeCombo()/mountTagTypeahead() just above, same
// destroy-and-recreate discipline: the compose textarea is torn down by every
// refreshPanelContent() repaint (comment post, tag/parent change, etc.), so the
// previous instance's DOM is already gone by the time this runs.
function mountMentionTypeaheads() {
  if (!currentState || !currentState.root) return;
  if (commentMentionTypeahead) { commentMentionTypeahead.destroy(); commentMentionTypeahead = null; }
  const ta = currentState.root.querySelector('.task-detail-comment-input');
  if (!ta) return;

  commentMentionTypeahead = attachMentionTypeahead(ta, {
    getMembers: () => (currentState ? currentState.members : []),
    scrollEl: detailScrollEl(currentState),
  });
}

// startCommentEdit(row) / cancelCommentEdit() / saveCommentEdit(id) (TPT33) — in-place
// DOM swap for editing an own comment, modeled on startDescriptionEdit() above. NEVER
// routed through refreshPanelContent(): a full repaint would (a) force "which comment
// is editing" into yet another module global purely to survive its own render, (b)
// wipe a half-typed NEW comment out of the compose box (the post handler gets away
// with clearing that box first — an unrelated edit must not), and (c) tear down the
// assignee combo / tag typeahead / any other live mention dropdown mid-interaction —
// exactly what mountAssigneeCombo()'s onCommit already documents avoiding.
function startCommentEdit(row) {
  if (!detailCan('comment:write')) return;
  if (!currentState || !row) return;
  const id = Number(row.dataset.commentId);
  if (!Number.isFinite(id)) return;
  if (editingCommentId != null) cancelCommentEdit(); // only one inline editor at a time

  const comment = comments.find(c => Number(c.id) === id);
  if (!comment) return;
  const bodyEl = row.querySelector('.task-detail-comment-body');
  const editBtn = row.querySelector('.task-detail-comment-edit-btn');
  if (!bodyEl) return;

  const rect = bodyEl.getBoundingClientRect();
  const wrap = document.createElement('div');
  wrap.className = 'task-detail-comment-edit';
  wrap.innerHTML = `
    <textarea class="form-textarea task-detail-comment-edit-input" rows="3"></textarea>
    <div class="task-detail-comment-edit-actions">
      <button type="button" class="btn btn-ghost btn-sm task-detail-comment-cancel-btn">Cancel</button>
      <button type="button" class="btn btn-primary btn-sm task-detail-comment-save-btn">Save</button>
    </div>`;
  const textarea = wrap.querySelector('.task-detail-comment-edit-input');
  textarea.value = comment.content || '';
  textarea.style.minHeight = Math.max(Math.ceil(rect.height), 72) + 'px';

  editingCommentId = id;
  if (editBtn) editBtn.style.display = 'none';
  bodyEl.replaceWith(wrap);
  textarea.focus();
  textarea.setSelectionRange(textarea.value.length, textarea.value.length);

  attachImageHandlers(textarea, currentState.projectId, currentState.taskKey);
  // Attached BEFORE the local Escape keydown listener below — load-bearing, same
  // ordering constraint startDescriptionEdit() documents (mention-typeahead.js's
  // onKeydown calls stopImmediatePropagation() while its dropdown is open, which only
  // suppresses a same-element listener registered after this one).
  editCommentMentionTypeahead = attachMentionTypeahead(textarea, {
    getMembers: () => (currentState ? currentState.members : []),
    scrollEl: detailScrollEl(currentState),
  });

  // No blur-to-save here (unlike the description field) — the task asks for explicit
  // Save/Cancel; clicking away leaves the editor open rather than silently committing.
  textarea.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') { e.stopPropagation(); cancelCommentEdit(); }
    else if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); saveCommentEdit(id); }
  });
}

function cancelCommentEdit() {
  if (!currentState || editingCommentId == null) return;
  const id = editingCommentId;
  const comment = comments.find(c => Number(c.id) === id);
  editingCommentId = null;
  if (editCommentMentionTypeahead) { editCommentMentionTypeahead.destroy(); editCommentMentionTypeahead = null; }
  if (comment) replaceCommentRow(id, comment);
}

async function saveCommentEdit(id) {
  if (!detailCan('comment:write')) return;
  if (!currentState || editingCommentId !== id) return;
  const row = currentState.root.querySelector(`.task-detail-comment[data-comment-id="${id}"]`);
  const textarea = row && row.querySelector('.task-detail-comment-edit-input');
  const saveBtn = row && row.querySelector('.task-detail-comment-save-btn');
  if (!textarea) return;
  const content = textarea.value.trim();
  if (!content) { showToast('Comment cannot be empty', 'error'); return; }

  const original = comments.find(c => Number(c.id) === id);
  if (original && content === (original.content || '').trim()) { cancelCommentEdit(); return; }

  const state = currentState; // snapshot — the panel/page can close mid-flight
  if (saveBtn) saveBtn.disabled = true;
  try {
    const { comment } = await api.patch(
      '/projects/' + state.projectId + '/tasks/' + state.taskKey + '/comments/' + id,
      { content }
    );
    if (currentState !== state) return; // stale result — a different task/panel owns state now
    const idx = comments.findIndex(c => Number(c.id) === id);
    if (idx >= 0) comments[idx] = comment; // updated_at now set -> "(edited)" renders
    editingCommentId = null;
    if (editCommentMentionTypeahead) { editCommentMentionTypeahead.destroy(); editCommentMentionTypeahead = null; }
    replaceCommentRow(id, comment);
  } catch (err) {
    if (saveBtn) saveBtn.disabled = false;
    showToast(err.message || 'Failed to save comment', 'error');
    // Leave the textarea + the user's text up — a 403/network blip must not eat their edit.
  }
}

// replaceCommentRow(id, comment) — pure DOM swap, no network: reuses renderCommentRow()
// so the post-edit/post-cancel row is byte-identical to what a full repaint would have
// produced.
function replaceCommentRow(id, comment) {
  if (!currentState || !currentState.root) return;
  const row = currentState.root.querySelector(`.task-detail-comment[data-comment-id="${id}"]`);
  if (!row) return;
  const tmp = document.createElement('div');
  tmp.innerHTML = renderCommentRow(comment, currentState.members).trim();
  row.replaceWith(tmp.firstElementChild);
}

// Change Parent (TPT618) — candidates are queried from the server one bounded summary page at a
// time (lib/parent-picker.js), never taken from options.allTasks, so every task in the project
// stays reachable whatever rows the caller holds. Typing re-queries (debounced); a reply that a
// newer query superseded, or that lands after the modal closed, is dropped.
function showParentPicker() {
  if (!detailCan('task:write')) return;
  if (!currentState) return;
  const owner = currentState;
  const { projectId, task } = owner;
  let candidates = new Map(); // id -> summary row currently listed
  let seq = 0;
  let closed = false;

  const html = `
    <input type="text" class="form-input mb-1" id="parent-picker-search" placeholder="Search tasks..." maxlength="200" autofocus>
    <div id="parent-picker-list" style="max-height:300px;overflow-y:auto">
      <div class="text-muted">Loading...</div>
    </div>`;

  detailModalToken = openModal('Select Parent', html, {
    onClose() { closed = true; },
    onMount(overlay) {
      const search = overlay.querySelector('#parent-picker-search');
      const list = overlay.querySelector('#parent-picker-list');

      const load = async () => {
        if (closed) return;
        const mySeq = ++seq;
        let rows;
        try {
          ({ tasks: rows } = await api.get(parentPickerPath(projectId, search.value)));
        } catch (err) {
          if (closed || mySeq !== seq) return;
          candidates = new Map();
          list.innerHTML = `<div class="text-muted">${escapeHtml(err.message || 'Failed to load tasks')}</div>`;
          return;
        }
        if (closed || mySeq !== seq) return;
        rows = rows || [];
        const eligible = parentPickerCandidates(rows, task);
        const full = rows.length >= PARENT_PICKER_LIMIT;
        candidates = new Map(eligible.map(t => [t.id, t]));
        list.innerHTML = eligible.length || full
          ? eligible.map(t => `
      <div class="task-detail-child parent-picker-item" data-task-id="${t.id}" data-task-key="${escapeHtml(t.task_key)}">
        ${getTypeIcon(t.type || 'task')}
        <span class="badge badge-sm">${escapeHtml(t.task_key)}</span>
        <span>${escapeHtml(t.title)}</span>
      </div>`).join('')
            + (full ? `<div class="text-muted text-sm">Showing the first ${PARENT_PICKER_LIMIT} tasks — type to narrow.</div>` : '')
          : '<div class="text-muted">No matching tasks</div>';
      };

      search.addEventListener('input', debounce(load, 250));
      load();

      list.addEventListener('click', async (e) => {
        const item = e.target.closest('.parent-picker-item');
        if (!item) return;
        const parent = candidates.get(parseInt(item.dataset.taskId, 10));
        closeModal();
        if (!parent || currentState !== owner) return;
        await patchField('parent_id', parent.id);
        if (currentState !== owner) return;
        // The new parent may sit outside the caller's rows: keep the picked row beside them so
        // the Parent field shows it at once. Only once the save landed — after a failed PATCH
        // the previously resolved parent stays.
        if (owner.task.parent_id === parent.id) owner.options.parentTask = parent;
        refreshPanelContent();
      });
    }
  });
}

function openAddChildModal() {
  if (!detailCan('task:write')) return;
  if (!currentState) return;
  const owner = currentState;
  const { projectId, task, members, statuses, project, options } = owner;
  const drawer = detailOwner;
  openCreateTaskModal(projectId, {
    parentTask: task,
    priority: task.priority,
    status: roleStatus(statuses, 'start')?.name || 'pending',
    allowedTypes: task.type === 'story' ? ['task', 'bug'] : undefined,
    project,
    // `{ priority }` rows for the modal's sprint numbers.
    sprintRows: sprintRowsOf(owner),
    members,
    sprints: options.sprints || [],
    statuses,
    isAlive: () => currentState === owner,
    onCreated: (newTask) => {
      // Into the drawer's own children list, not options.allTasks: on a board that is the
      // owner's filtered page (C1261), which its reload on drawer close replaces anyway.
      owner.children = [...(owner.children || []), newTask];
      refreshPanelContent();
    },
  }).then((token) => {
    // Only while the drawer (or page) that asked for it is still the one open.
    if (token && currentState === owner && detailOwner === drawer) detailModalToken = token;
  });
}

function confirmDelete() {
  if (!detailCan('task:delete')) return;
  if (!currentState) return;
  const { projectId, taskKey } = currentState;

  detailModalToken = openModal('Delete Task', `
    <p>Delete task <strong>${escapeHtml(taskKey)}</strong>?</p>
    <p class="text-muted mt-1">This cannot be undone.</p>
    <div class="modal-footer">
      <button class="btn btn-ghost" id="modal-cancel">Cancel</button>
      <button class="btn btn-danger" id="confirm-delete-btn">Delete</button>
    </div>
  `, {
    onMount(overlay) {
      overlay.querySelector('#modal-cancel').addEventListener('click', closeModal);
      overlay.querySelector('#confirm-delete-btn').addEventListener('click', async () => {
        const btn = overlay.querySelector('#confirm-delete-btn');
        btn.disabled = true;
        btn.textContent = 'Deleting...';
        try {
          await api.delete('/projects/' + projectId + '/tasks/' + taskKey);

          // Remove from allTasks
          if (currentState.options.allTasks) {
            const idx = currentState.options.allTasks.findIndex(t => t.task_key === taskKey);
            if (idx >= 0) currentState.options.allTasks.splice(idx, 1);
          }

          closeModal();
          showToast('Task deleted', 'success');
          if (currentState && currentState.mode === 'page') {
            const pid = currentState.projectId;
            currentState = null;
            navigate('/projects/' + pid + '/board');
          } else {
            closeTaskDetail();
          }
        } catch (err) {
          btn.disabled = false;
          btn.textContent = 'Delete';
          showToast(err.message || 'Failed to delete', 'error');
        }
      });
    }
  });
}

// mountAssigneeCombo() — (re)wires the Member Assignee combo-box after any paint that
// replaces the rendered pane content. Parent changes, comment posts, and
// Notifications refreshes call refreshPanelContent(), so the combo
// instance from the previous paint is dead DOM by the time this fires — destroy it and
// mount a fresh one against the freshly rendered `.task-detail-assignee-combo` root.
function mountAssigneeCombo() {
  if (!currentState || !currentState.root) return;
  const comboRoot = currentState.root.querySelector('.task-detail-assignee-combo');
  if (assigneeCombo) { assigneeCombo.destroy(); assigneeCombo = null; }
  if (!comboRoot) return;

  assigneeCombo = createMemberCombo(comboRoot, {
    getMembers: () => (currentState ? currentState.members : []),
    getValue: () => (currentState ? currentState.task.assignee : null),
    setValue: (id) => { if (currentState) currentState.task.assignee = id; },
    onCommit: (userId) => {
      if (!currentState) return;
      // Update the sibling avatar in place — deliberately not refreshPanelContent(),
      // which would tear down this very combo mid-interaction and steal focus.
      const avatarEl = currentState.root.querySelector('.task-detail-assignee-avatar');
      if (avatarEl) {
        const member = userId ? (currentState.members.find(m => m.user_id === userId) || null) : null;
        avatarEl.innerHTML = renderAvatarHtml(member, 24);
      }
      const inputEl = comboRoot.querySelector('.member-combo-input');
      patchField('assignee', userId, inputEl);
    },
    isAlive: () => document.body.contains(comboRoot),
    // The reposition listener has to hear whichever element is actually scrolling: the pane
    // scroller or the drawer itself in the panel, the document on the page (detailScrollEl()).
    scrollEl: detailScrollEl(currentState),
  });
}

function refreshPanelContent() {
  if (!currentState || !currentState.root) return;
  // The rows the Notifications tab's IntersectionObserver is watching are about to be
  // discarded by the innerHTML rewrite below — disconnect first, re-arm after if that
  // tab is still the active one (TPT18).
  disconnectEventObserver();
  // A live inline comment editor (TPT33) is about to be discarded by the innerHTML
  // rewrite below too — drop its state so nothing (the editing flag, its mention
  // dropdown) survives pointing at DOM that's about to be gone.
  editingCommentId = null;
  if (editCommentMentionTypeahead) { editCommentMentionTypeahead.destroy(); editCommentMentionTypeahead = null; }
  const children = currentState.children || [];
  const fresh = document.createElement('div');
  fresh.innerHTML = renderPanelContent(currentState.task, currentState.members, children);
  // Keep the tab strip (and the scrolling body's position) mounted while replacing
  // content. Clicks are delegated from currentState.root, including after this repaint.
  for (const selector of ['.task-detail-header', '.task-detail-intro', '.task-detail-tab-content', '.task-detail-footer']) {
    currentState.root.querySelector(selector).replaceWith(fresh.querySelector(selector));
  }
  for (const nextButton of fresh.querySelectorAll('.task-detail-tab')) {
    const button = currentState.root.querySelector(`.task-detail-tab[data-tab="${nextButton.dataset.tab}"]`);
    button.innerHTML = nextButton.innerHTML;
    button.className = nextButton.className;
    button.setAttribute('aria-selected', nextButton.getAttribute('aria-selected'));
  }
  const commentInput = currentState.root.querySelector('.task-detail-comment-input');
  if (commentInput) attachImageHandlers(commentInput, currentState.projectId, currentState.taskKey);
  mountAssigneeCombo();
  mountTagTypeahead();
  mountDependencyTypeahead();
  mountMentionTypeaheads();
  if (activeTab === 'notifications') observeUnreadEvents();
}

// switchTab(tab) — (TPT18) toggles the Details/Comments/Notifications tab strip's
// active button + which `.task-detail-tab-panel` is visible, without a full repaint
// (renderPanelContent() only runs on data changes, not on a tab click). `activeTab` is
// a module global (not part of currentState) for the same reason `comments` is:
// refreshPanelContent() replaces the rendered panes, and the freshly
// rendered markup reads `activeTab` to restore whichever tab was showing.
function switchTab(tab) {
  if (!currentState || !currentState.root) return;
  activeTab = tab;
  // The compact drawer scrolls as a whole (TPT9214). Its position is put back after the panes swap:
  // the browser's scroll anchoring would otherwise move it to follow content of the pane going away.
  const compact = currentState.root === panelEl && isCompactDrawer();
  const keepTop = compact ? panelEl.scrollTop : 0;
  currentState.root.querySelectorAll('.task-detail-tab').forEach(btn => {
    const isActive = btn.dataset.tab === tab;
    btn.classList.toggle('active', isActive);
    btn.setAttribute('aria-selected', String(isActive));
  });
  currentState.root.querySelectorAll('.task-detail-tab-panel').forEach(panel => {
    panel.hidden = panel.dataset.tab !== tab;
  });
  const content = currentState.root.querySelector('.task-detail-tab-content');
  if (compact) {
    panelEl.scrollTop = keepTop;
    alignCompactPane(content);
  } else if (content) content.scrollTop = 0;
  if (tab === 'notifications') observeUnreadEvents();
  else disconnectEventObserver();
}

// observeUnreadEvents()/disconnectEventObserver() — (TPT18) per-row "seen" tracking for
// the Notifications tab, adapted from the Task App modal's comment-seen IntersectionObserver
// (task-board.js `_observeVisibleComments`). Only rows still unread-and-unobserved are
// watched; once a row scrolls into view it's marked observed and the tab badge is
// recomputed in place (no full repaint, so the observer isn't torn down mid-scroll).
// The last-seen marker is persisted only once EVERY currently-unread row has been seen
// this session — a single high-water id, so a partial scroll never marks later, still-
// unseen rows as read.
function observeUnreadEvents() {
  disconnectEventObserver();
  if (!currentState || !currentState.root) return;
  const listEl = currentState.root.querySelector('.task-detail-events-list');
  if (!listEl) return;

  const currentUser = getUser();
  const seenEventId = getSeenEventId(currentState.projectId, currentState.taskKey);
  const unreadIds = new Set(
    unreadEventIds(taskEvents, seenEventId, currentUser && currentUser.id)
      .filter(id => !observedEventIds.has(id))
  );
  if (unreadIds.size === 0) return;

  // The implicit root (the viewport) clips a row by every scroller around it: the pane scroller
  // in the desktop drawer, the drawer itself in the compact one (TPT9214), the document on the
  // page. One root for every layout, so a layout change while the tab is open cannot leave the
  // observer watching an element that no longer scrolls (which would count every row as seen).
  const root = null;

  eventObserver = new IntersectionObserver((entries) => {
    let changed = false;
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      const id = Number(entry.target.dataset.eventId);
      if (!unreadIds.has(id)) continue;
      unreadIds.delete(id);
      observedEventIds.add(id);
      eventObserver.unobserve(entry.target);
      changed = true;
    }
    if (!changed || !currentState || !currentState.root) return;
    const badgeHost = currentState.root.querySelector('.task-detail-tab[data-tab="notifications"]');
    if (badgeHost) {
      const remaining = unreadIds.size;
      let badge = badgeHost.querySelector('.task-detail-tab-badge');
      if (remaining === 0) {
        if (badge) badge.remove();
        setSeenEventId(currentState.projectId, currentState.taskKey, maxEventId(taskEvents));
      } else {
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'task-detail-tab-badge';
          badgeHost.appendChild(badge);
        }
        badge.textContent = remaining > 99 ? '99+' : String(remaining);
      }
    }
  }, { root, threshold: 0.1 });

  listEl.querySelectorAll('.task-detail-event[data-event-id]').forEach(row => {
    if (unreadIds.has(Number(row.dataset.eventId))) eventObserver.observe(row);
  });
}

function disconnectEventObserver() {
  if (eventObserver) { eventObserver.disconnect(); eventObserver = null; }
}

// Repaints the section alone, in place, and keeps keyboard focus on its control. `focusKey` names
// the control that should take focus instead, when the repaint itself moves on from the old one,
// unless the user has gone elsewhere meanwhile: a slow start must not pull them out of a comment.
function syncRemoteControls(focusKey = null) {
  if (!currentState || !currentState.root || !remoteView || remoteView.root !== currentState.root) return;
  const host = currentState.root.querySelector('.remote-session-host');
  if (!host) return;
  const restore = noteFocusBeforeRender(host, el => (el.dataset?.remoteFocus ? `[data-remote-focus="${el.dataset.remoteFocus}"]` : null));
  const hadFocus = host.contains(document.activeElement) || document.activeElement === document.body;
  host.innerHTML = renderRemoteSessionControls(currentState.task, remoteView, remoteContext());
  if (!focusKey) restore();
  else if (hadFocus) host.querySelector(`[data-remote-focus="${focusKey}"]`)?.focus();
}

// Starts the section for the task `state` shows, ahead of the first paint so it renders with it.
// Every reader watches attribution, including other members' devices. The online-device read
// separately gates controls for devices this viewer owns. (TPT9217) One project watch serves
// both, and the controls hear every relay event from the start: a device that comes online after
// the first online-device read announces itself only through the relay's `device` frame.
function startRemoteControls(state) {
  stopRemoteControls();
  const scope = createDeviceSessionScope(state.projectId);
  const watch = { root: state.root, scope, relinking: false, unwatch: null };
  attributionView = watch;
  watch.unwatch = relayClient.watchProject(Number(state.projectId), event => {
    if (currentState !== state || attributionView !== watch) return;
    if (applyDeviceSessionEvent([state.task], event, scope)) {
      syncDeviceBadges(state.root, [state.task], state.project?.language);
      syncRemoteControls();
    }
    if (remoteView) onRemoteEvent(remoteView, event);
    else if (event.type === 'device' && event.online) relinkRemoteControls(state, watch);
  });
  openRemoteControls(state, scope);
}

// (TPT9217) A device came online while this task shows no device section, because none of this
// viewer's devices linked to the project when the page read them. Links change outside this page:
// Task App setup on another machine, or Task App linking its own device when a project opts in to
// remote control. Re-read them, once at a time, and open the section if one links now.
function relinkRemoteControls(state, watch) {
  if (watch.relinking || !canRunOnDevice(state.task)) return;
  watch.relinking = true;
  reloadDevices().then(devices => {
    watch.relinking = false;
    if (currentState !== state || attributionView !== watch || remoteView) return;
    state.devices = devices;
    openRemoteControls(state, watch.scope);
  });
}

// The section's own state, for a task this viewer may run on a device of theirs that links to the
// project. Sessions and the snapshot flag start from the attribution scope, which has heard every
// relay event for this project so far: a section opened late must not wait for a new snapshot.
function openRemoteControls(state, scope) {
  if (!canRunOnDevice(state.task)) return;
  const projectId = String(state.projectId);
  const linked = (state.devices || []).some(d => (d.project_ids || []).some(id => String(id) === projectId));
  if (!linked) return;
  remoteView = {
    root: state.root,
    projectId: Number(state.projectId),
    status: 'checking', // checking | ready | failed | revoked
    checking: false,
    fetchSeq: 0,
    refreshTimer: null,
    devices: [],
    sessions: new Map(scope.sessions),
    subscribed: scope.subscribed,
    canWrite: null,
    connection: relayClient.connection,
    deviceId: null,
    agent: '',
    starting: false,
    stopping: new Set(),
    confirmStopId: null,
    autoAttachId: null,
    autoAttachUntil: 0,
    error: null,
  };
  syncRemoteControls();
  refreshRemoteDevices(remoteView);
}

// Ends the section: closes the terminal, drops the subscription and every timer. No session stops.
function stopRemoteControls() {
  attributionView?.unwatch?.();
  attributionView = null;
  closeRemoteTerminal();
  const view = remoteView;
  if (!view) return;
  remoteView = null;
  clearTimeout(view.refreshTimer);
}

async function refreshRemoteDevices(view) {
  const seq = ++view.fetchSeq;
  view.checking = true;
  syncRemoteControls();
  const devices = await loadOnlineDevices(view.projectId).catch(() => null);
  if (remoteView !== view || seq !== view.fetchSeq) return;
  view.checking = false;
  if (devices) {
    view.devices = devices;
    if (view.status !== 'revoked') view.status = 'ready';
  } else if (view.status === 'checking') {
    view.status = 'failed';
  }
  syncRemoteControls();
}

function onRemoteEvent(view, event) {
  if (remoteView !== view) return;
  switch (event.type) {
    case 'connection': {
      // (TPT9217) The relay announces presence changes as they happen and never repeats them, so
      // devices that came or went while the link was down are re-read once it is back.
      const recovered = event.connection === 'open' && RELAY_RECOVERING.has(view.connection);
      view.connection = event.connection;
      // Until the next snapshot nobody knows what runs where, and Start waits for it.
      if (event.connection !== 'open') view.subscribed = false;
      if (recovered) scheduleRemoteRefresh(view);
      break;
    }
    case 'subscribed':
      view.subscribed = true;
      view.canWrite = event.canWrite;
      view.sessions = new Map(event.sessions.map(session => [session.session_id, session]));
      if (!view.sessions.has(view.confirmStopId)) view.confirmStopId = null;
      break;
    case 'session':
      if (isLiveSession(event.session)) {
        view.sessions.set(event.session.session_id, event.session);
        // A start the relay gave up on (device_timeout) may still have landed.
        if (event.session.task_key === currentState?.taskKey) view.error = null;
      } else {
        view.sessions.delete(event.session.session_id);
        if (view.confirmStopId === event.session.session_id) view.confirmStopId = null;
        // The device lost this project, which the online list has not caught up with.
        if (event.session.state === 'revoked') scheduleRemoteRefresh(view);
      }
      break;
    case 'device':
      scheduleRemoteRefresh(view);
      return;
    case 'revoked':
      view.status = 'revoked';
      view.sessions = new Map();
      view.confirmStopId = null;
      break;
    default:
      return;
  }
  syncRemoteControls();
  autoAttachStarted(view);
}

function scheduleRemoteRefresh(view) {
  if (view.refreshTimer !== null) return;
  view.refreshTimer = setTimeout(() => {
    view.refreshTimer = null;
    refreshRemoteDevices(view);
  }, DEVICE_REFRESH_MS);
}

// The terminal of a session this user just started, once it has one.
function autoAttachStarted(view) {
  const session = view.sessions.get(view.autoAttachId);
  if (session?.state !== 'running') return;
  const inTime = Date.now() <= view.autoAttachUntil;
  view.autoAttachId = null;
  if (inTime && !remoteTerminal && !document.querySelector('.modal-overlay')) openRemoteTerminal(session.session_id);
}

// The Start button. Off while one is in flight, and the relay client resends the same command id
// across a reconnect, so a double click or a dropped link cannot run the task twice. The relay
// answers already_running with the session it found, which is then attached instead.
async function startRemoteSession(view) {
  if (view.starting || !currentState) return;
  const { task } = currentState;
  const device = startableDevices(view.devices, view.projectId).find(d => d.id === view.deviceId)
    ?? startableDevices(view.devices, view.projectId)[0];
  if (!device) return;
  const language = remoteContext().language;
  const agent = device.capabilities?.agents?.includes(view.agent) ? view.agent : null;
  view.starting = true;
  view.error = null;
  syncRemoteControls();
  const result = await relayClient.start({ deviceId: device.id, projectId: view.projectId, taskKey: task.task_key, agent });
  view.starting = false;
  if (remoteView !== view) return;
  if (result.ok && result.session_id) {
    view.autoAttachId = result.session_id;
    view.autoAttachUntil = Date.now() + AUTO_ATTACH_WINDOW_MS;
  } else if (result.code === 'already_running' && view.sessions.has(result.session_id)) {
    view.autoAttachId = result.session_id;
    view.autoAttachUntil = Date.now() + AUTO_ATTACH_WINDOW_MS;
  } else if (!result.ok) {
    view.error = remoteCodeText(language, result.code);
  }
  syncRemoteControls('start');
  autoAttachStarted(view);
}

async function stopRemoteSession(view, sessionId) {
  const language = remoteContext().language;
  view.confirmStopId = null;
  view.stopping.add(sessionId);
  view.error = null;
  syncRemoteControls(`stop:${sessionId}`);
  const result = await relayClient.stop(sessionId);
  view.stopping.delete(sessionId);
  if (remoteView !== view) return;
  if (!result.ok) {
    view.error = remoteCodeText(language, result.code);
    if (remoteTerminal?.binding) {
      showToast(view.error, 'error');
      paintRemoteTerminal(remoteTerminal, remoteTerminal.binding.state);
    }
  }
  syncRemoteControls();
}

// The drawer's and the page's buttons in the section, through the surface's delegated click.
function onRemoteClick(button) {
  const view = remoteView;
  if (!view) return;
  const sessionId = button.closest('[data-session-id]')?.dataset.sessionId;
  if (button.matches('.remote-session-start')) startRemoteSession(view);
  else if (button.matches('.remote-session-attach')) openRemoteTerminal(sessionId);
  else if (button.matches('.remote-session-stop')) {
    view.confirmStopId = sessionId;
    syncRemoteControls(`keep:${sessionId}`); // The safe answer takes focus, not the destructive one.
  } else if (button.matches('.remote-session-stop-keep')) {
    view.confirmStopId = null;
    syncRemoteControls(`stop:${sessionId}`);
  } else if (button.matches('.remote-session-stop-confirm')) stopRemoteSession(view, sessionId);
  else if (button.matches('.remote-session-refresh')) {
    if (view.status === 'failed') view.status = 'checking';
    refreshRemoteDevices(view);
  } else if (button.matches('.remote-session-retry')) relayClient.retry();
}

function remoteTerminalMarkup(session, taskKey, language, canStop) {
  const text = (key, params) => escapeHtml(remoteText(language, key, params));
  return `<div class="remote-terminal">
      <div class="remote-terminal-bar">
        <span class="remote-terminal-status" role="status">
          <span class="remote-session-dot" aria-hidden="true"></span>
          <span class="remote-terminal-state">${text('connecting')}</span>
        </span>
        <span class="remote-terminal-role"></span>
        <span class="remote-terminal-agent">${escapeHtml(agentLabel(session.agent))}</span>
        ${canStop ? `<button type="button" class="btn btn-ghost btn-sm remote-terminal-stop">${text('stop')}</button>` : ''}
      </div>
      <div class="remote-terminal-confirm" hidden>${canStop ? renderStopConfirm(session.session_id, taskKey, session.device_name, language) : ''}</div>
      <div class="remote-terminal-notice" role="alert" hidden>
        <span class="remote-terminal-notice-text"></span>
        <button type="button" class="btn btn-ghost btn-sm remote-terminal-retry" hidden>${text('tryAgain')}</button>
      </div>
      <div class="remote-terminal-host" role="group" aria-label="${text('terminalLabel', { task: taskKey })}"></div>
      <p class="remote-terminal-hint">${text('hintReadOnly')}</p>
    </div>`;
}

// Paints the dialog's status bar, banner and hint for a terminal state. textContent throughout.
function paintRemoteTerminal(view, state) {
  const { overlay, language } = view;
  const text = key => remoteText(language, key);
  const status = terminalStatus(state);
  overlay.querySelector('.remote-terminal-status .remote-session-dot').className = `remote-session-dot remote-session-dot--${status.tone}`;
  overlay.querySelector('.remote-terminal-state').textContent = text(status.labelKey);
  overlay.querySelector('.remote-terminal-role').textContent = status.roleKey ? text(status.roleKey) : '';
  overlay.querySelector('.remote-terminal-hint').textContent = text(terminalCanType(state) ? 'hintControl' : 'hintReadOnly');
  const notice = status.code ? remoteCodeText(language, status.code)
    : status.noticeKey ? remoteText(language, status.noticeKey, status.noticeParams) : '';
  showRemoteTerminalNotice(overlay, notice, status.retry);
  const stop = overlay.querySelector('.remote-terminal-stop');
  if (stop) {
    stop.hidden = !!state.ended;
    stop.disabled = !!remoteView?.stopping.has(view.sessionId) || !terminalCanStop(state);
    if (state.ended) overlay.querySelector('.remote-terminal-confirm').hidden = true;
  }
}

function showRemoteTerminalNotice(overlay, message, retry = false) {
  overlay.querySelector('.remote-terminal-notice').hidden = !message;
  overlay.querySelector('.remote-terminal-notice-text').textContent = message;
  overlay.querySelector('.remote-terminal-retry').hidden = !retry;
}

function onRemoteTerminalClick(view, event) {
  const button = event.target.closest('button');
  if (!button) return;
  const confirm = view.overlay.querySelector('.remote-terminal-confirm');
  if (button.matches('.remote-terminal-stop')) {
    confirm.hidden = false;
    confirm.querySelector('.remote-session-stop-keep').focus();
  } else if (button.matches('.remote-session-stop-keep')) {
    confirm.hidden = true;
    view.overlay.querySelector('.remote-terminal-stop').focus();
  } else if (button.matches('.remote-session-stop-confirm')) {
    confirm.hidden = true;
    view.overlay.querySelector('.remote-terminal-stop').disabled = true;
    view.term?.focus();
    if (remoteView) stopRemoteSession(remoteView, view.sessionId);
  } else if (button.matches('.remote-terminal-retry')) {
    relayClient.retry();
  }
}

// The dialog for one session. It sits on ui.js's modal with its owner token in detailModalToken,
// so closeTaskDetail() and mountTaskDetail()'s unmount close it (§ Drawer ownership), and every way
// out runs disposeRemoteTerminal() from onClose. Only the first terminal of a page load fetches
// xterm. Attaching never starts anything, so a session runs once however often it is opened.
export async function openRemoteTerminal(sessionId) {
  const session = remoteView?.sessions.get(sessionId);
  if (!currentState || !session) return;
  closeRemoteTerminal();
  const { language, canWrite, userId } = remoteContext();
  const taskKey = currentState.taskKey;
  const view = { sessionId, session, language, overlay: null, token: null, binding: null, term: null, observer: null, disposed: false };
  remoteTerminal = view;
  const title = remoteText(language, 'terminalTitle', { task: taskKey, device: session.device_name });
  const canStop = canWrite && session.owner_id === userId;
  view.token = openModal(title, remoteTerminalMarkup(session, taskKey, language, canStop), {
    onClose() {
      disposeRemoteTerminal(view);
      const attach = remoteView && currentState?.root?.querySelector(`[data-remote-focus="attach:${sessionId}"]`);
      if (attach && document.activeElement === document.body) attach.focus();
    },
    onMount(overlay) {
      view.overlay = overlay;
      overlay.querySelector('.modal').classList.add('remote-terminal-modal');
      overlay.addEventListener('click', event => onRemoteTerminalClick(view, event));
    },
  });
  detailModalToken = view.token;

  let xterm;
  try {
    xterm = await loadXterm();
  } catch {
    if (!view.disposed) showRemoteTerminalNotice(view.overlay, remoteText(language, 'noticeLoadFailed'));
    return;
  }
  if (view.disposed) return;

  const host = view.overlay.querySelector('.remote-terminal-host');
  const term = new xterm.Terminal({
    fontSize: 13,
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    scrollback: 5000,
    theme: TERMINAL_THEME,
    // Only OSC 8 links are live, since no link addon is loaded. xterm's default opener would set
    // a popup's location to whatever URI the terminal data names.
    linkHandler: {
      activate(event, uri) {
        const href = safeTerminalLink(uri);
        if (href) window.open(href, '_blank', 'noopener,noreferrer');
      },
    },
  });
  const fit = new xterm.FitAddon();
  term.loadAddon(fit);
  term.open(host);
  fit.fit();
  view.term = term;
  view.binding = bindRemoteTerminal({
    client: relayClient,
    sessionId,
    session,
    term,
    fit,
    onChange: state => paintRemoteTerminal(view, state),
    onClose: () => closeRemoteTerminal(),
    onInputRefused: code => showToast(remoteCodeText(language, code), 'error'),
  });
  view.observer = new ResizeObserver(() => view.binding?.requestFit());
  view.observer.observe(host);
  paintRemoteTerminal(view, view.binding.state);
  term.focus();
}

function closeRemoteTerminal() {
  if (remoteTerminal) closeModal(remoteTerminal.token);
}

// Tears the terminal down and leaves the session running: the relay is told to detach, so the
// device stops streaming to this browser unless another viewer is attached. Safe to repeat. With
// no argument it ends whichever terminal is open; with one, only that terminal.
export function disposeRemoteTerminal(view = remoteTerminal) {
  if (!view || view.disposed) return;
  view.disposed = true;
  if (remoteTerminal === view) remoteTerminal = null;
  view.observer?.disconnect();
  view.binding?.dispose();
  view.term?.dispose();
}

// bindDetailEvents(el) — binds the 3 delegated listeners to `el` (panelEl for the
// slide-out, a page host for the standalone route) and returns an unbind fn. Every
// handler opens with an ownership check: currentState.root !== e.currentTarget means
// this surface doesn't currently own currentState, so it no-ops. Without this guard,
// binding the same handlers to two live elements would let a click on one surface
// mutate state owned by the other.
function bindDetailEvents(el) {
  if (!el) return () => {};

  const onClick = (e) => {
    if (!currentState || currentState.root !== e.currentTarget) return;

    // Remote sessions (TPT495)
    const remoteButton = e.target.closest('.remote-session-host button');
    if (remoteButton) {
      onRemoteClick(remoteButton);
      return;
    }

    // Tab strip (TPT18) — Details / Comments / Notifications
    const tabBtn = e.target.closest('.task-detail-tab');
    if (tabBtn) {
      switchTab(tabBtn.dataset.tab);
      return;
    }

    // Notifications tab: manual refresh
    if (e.target.closest('.task-detail-events-refresh')) {
      const btn = e.target.closest('.task-detail-events-refresh');
      const { projectId, taskKey } = currentState;
      btn.disabled = true;
      api.get('/projects/' + projectId + '/tasks/' + taskKey + '/events?limit=100')
        .then((res) => {
          taskEvents = res.events || [];
          eventsSubscribed = res.subscribed !== false;
          refreshPanelContent();
        })
        .catch(err => showToast(err.message || 'Failed to load notifications', 'error'))
        .finally(() => { btn.disabled = false; });
      return;
    }

    // Close button (panel mode only — page mode never renders it)
    if (e.target.closest('.task-detail-close')) {
      closeTaskDetail();
      return;
    }

    // Clear the due date (TPT589) — a programmatic value change fires no `change`, so queue it here
    if (e.target.closest('.task-detail-due-clear')) {
      const input = currentState.root.querySelector('.task-detail-due-input');
      if (input) {
        input.value = '';
        queueDueSave(input);
      }
      return;
    }

    // Title click → edit
    if (e.target.closest('.task-detail-title') && !e.target.closest('[contenteditable="true"]')) {
      startTitleEdit();
      return;
    }

    // Description click → edit
    if (e.target.closest('.task-detail-description')) {
      startDescriptionEdit(e);
      return;
    }

    // Depends on (TPT9213): retry a failed check or save, or move this task after a dependency.
    if (e.target.closest('.task-detail-deps-retry')) {
      syncDependencies(currentState);
      return;
    }
    const moveBtn = e.target.closest('.task-detail-deps-move');
    if (moveBtn) {
      const owner = currentState;
      const floor = Number(moveBtn.dataset.floor);
      if (!Number.isInteger(floor) || floor <= 0) return;
      moveBtn.disabled = true;
      patchField('priority', floor, moveBtn, owner).then((ok) => {
        if (currentState !== owner) return;
        if (!ok) { moveBtn.disabled = false; return; }
        // The Sprint select may not list the new sprint yet: repaint, then keep keyboard focus on
        // the field the move changed.
        refreshPanelContent();
        owner.root.querySelector('.task-detail-priority-select')?.focus();
      });
      return;
    }

    // Remove parent
    if (e.target.closest('.task-detail-remove-parent')) {
      // Repaint only the drawer that asked: a late reply must not repaint (and wipe a half-typed
      // date in) the task the user opened next.
      const owner = currentState;
      patchField('parent_id', null).then(() => { if (currentState === owner) refreshPanelContent(); });
      return;
    }

    // Change parent
    if (e.target.closest('.task-detail-change-parent')) {
      showParentPicker();
      return;
    }

    // Parent link click → navigate to parent (page mode: real <a href> handles it natively)
    const parentLink = e.target.closest('.detail-parent-link');
    if (parentLink) {
      if (currentState.mode === 'page') return;
      const parentKey = parentLink.dataset.taskKey;
      if (parentKey) {
        // Read before closing: closeTaskDetail() clears currentState.
        const { projectId, options } = currentState;
        const owner = detailOwner;
        closeTaskDetail();
        setTimeout(() => {
          openPanel(projectId, parentKey, options, owner || {});
        }, 220);
      }
      return;
    }

    // Child click → navigate to child (page mode: real <a href> handles it natively)
    const childEl = e.target.closest('.task-detail-child:not(.parent-picker-item)');
    if (childEl) {
      if (currentState.mode === 'page') return;
      const childKey = childEl.dataset.taskKey;
      if (childKey) {
        // Read before closing: closeTaskDetail() clears currentState.
        const { projectId, options } = currentState;
        const owner = detailOwner;
        closeTaskDetail();
        setTimeout(() => {
          openPanel(projectId, childKey, options, owner || {});
        }, 220);
      }
      return;
    }

    // Add child
    if (e.target.closest('.task-detail-add-child')) {
      openAddChildModal();
      return;
    }

    // Delete
    if (e.target.closest('.task-detail-delete-btn')) {
      confirmDelete();
      return;
    }

    // Comment submit
    if (e.target.closest('.task-detail-comment-submit')) {
      if (!detailCan('comment:write')) return;
      const btn = e.target.closest('.task-detail-comment-submit');
      const ta = currentState.root.querySelector('.task-detail-comment-input');
      const content = ta?.value.trim();
      if (!content) return;
      btn.disabled = true;
      api.post('/projects/' + currentState.projectId + '/tasks/' + currentState.taskKey + '/comments', { content })
        .then(({ comment }) => { comments.push(comment); ta.value = ''; refreshPanelContent(); })
        .catch(err => showToast(err.message || 'Failed to post comment', 'error'))
        .finally(() => { btn.disabled = false; });
      return;
    }

    // Comment edit (TPT33) — Edit / Save / Cancel on an own comment's inline editor.
    // Distinct class names (not `-edit`/`-edit-save`) so closest() token matching is
    // never ambiguous between the three.
    if (e.target.closest('.task-detail-comment-edit-btn')) {
      startCommentEdit(e.target.closest('.task-detail-comment'));
      return;
    }
    if (e.target.closest('.task-detail-comment-save-btn')) {
      saveCommentEdit(editingCommentId);
      return;
    }
    if (e.target.closest('.task-detail-comment-cancel-btn')) {
      cancelCommentEdit();
      return;
    }
  };

  const onChange = (e) => {
    if (!currentState || currentState.root !== e.currentTarget) return;

    // Remote sessions (TPT495): only the choice is kept; Start reads it.
    if (remoteView && e.target.closest('.remote-session-device-select')) {
      remoteView.deviceId = Number(e.target.value);
      syncRemoteControls();
      return;
    }
    if (remoteView && e.target.closest('.remote-session-agent-select')) {
      remoteView.agent = e.target.value;
      return;
    }

    // Type select
    if (e.target.closest('.task-detail-type-select')) {
      patchField('type', e.target.value);
      return;
    }

    // Status select
    if (e.target.closest('.task-detail-status-select')) {
      const newStatus = e.target.value;
      const inProgressName = roleStatus(currentState.statuses, 'in_progress')?.name;
      if (newStatus === inProgressName && !canStartTask(currentState.task, getUser())) {
        showToast('Cannot start a task assigned to another member', 'error');
        e.target.value = currentState.task.status; // reset select
        return;
      }
      patchField('status', newStatus);
      return;
    }

    // Priority select — only rendered when project.sprints_enabled (C1334); no guard needed
    // here, an absent select means this branch is structurally unreachable.
    if (e.target.closest('.task-detail-priority-select')) {
      const owner = currentState;
      patchField('priority', parseInt(e.target.value, 10)).then(() => syncDependencyFloor(owner));
      return;
    }

    // ── Fields added by TPT9213 ── Each saves on change with its control disabled meanwhile, and
    // shows the saved value afterwards (a failure toasts and restores it).
    if (e.target.closest('.task-detail-category-select')) {
      const input = e.target;
      const owner = currentState;
      const finishSave = disableForSave(input);
      patchField('category', input.value, input, owner).then(() => {
        finishSave();
        if (currentState === owner) input.value = owner.task.category === 'HUMAN' ? 'HUMAN' : 'CODING';
      });
      return;
    }

    if (e.target.closest('.task-detail-agent-select')) {
      const input = e.target;
      const owner = currentState;
      const finishSave = disableForSave(input);
      patchField('agent_assignee', input.value || null, input, owner).then(() => {
        finishSave();
        if (currentState !== owner) return;
        input.value = AGENT_ASSIGNEES.includes(owner.task.agent_assignee) ? owner.task.agent_assignee : '';
        syncAgentFields();
      });
      return;
    }

    if (e.target.closest('.task-detail-effort-select')) {
      const input = e.target;
      const owner = currentState;
      const finishSave = disableForSave(input);
      patchField('effort', input.value || null, input, owner).then(() => {
        finishSave();
        if (currentState === owner) input.value = owner.task.effort || '';
      });
      return;
    }

    if (e.target.closest('.task-detail-design-toggle')) {
      const input = e.target;
      const owner = currentState;
      const finishSave = disableForSave(input);
      patchField('claude_design_mode', input.checked, input, owner).then(() => {
        finishSave();
        if (currentState === owner) input.checked = !!owner.task.claude_design_mode;
      });
      return;
    }

    // A model id commits on change (blur or Enter); blank saves null — the agent's default.
    if (e.target.closest('.task-detail-model-input')) {
      const input = e.target;
      const owner = currentState;
      const field = input.dataset.modelField;
      const value = modelInputValue(input.value);
      if (!['claude_model', 'codex_model', 'pi_model'].includes(field) || value === (owner.task[field] ?? null)) {
        input.value = owner.task[field] || '';
        return;
      }
      patchField(field, value, input, owner).then(() => {
        if (currentState === owner && input !== document.activeElement) input.value = owner.task[field] || '';
      });
      return;
    }

    if (e.target.closest('.task-detail-objective-toggle')) {
      const input = e.target;
      const owner = currentState;
      const finishSave = disableForSave(input);
      patchField('is_objective', input.checked, input, owner).then(() => {
        finishSave();
        if (currentState !== owner) return;
        input.checked = !!owner.task.is_objective;
        repaintChildren(owner);
      });
      return;
    }

    if (e.target.closest('.task-detail-urgency-select')) {
      const input = e.target;
      const owner = currentState;
      const finishSave = disableForSave(input);
      patchField('urgency', Number(input.value), input, owner).then(() => {
        if (currentState !== owner) return;
        input.value = String(owner.task.urgency ?? 0);
        finishSave();
      });
      return;
    }

    // Due date (TPT589) — queued, see queueDueSave()
    if (e.target.closest('.task-detail-due-input')) {
      queueDueSave(e.target);
      return;
    }

    // Permission mode (TPT9063) — "" is Project Default and saves an explicit null (an omitted
    // field would preserve the override). The select is passed explicitly: patchField()'s default
    // lookup would never match `.task-detail-agent_permission_mode-select` and fall to the next
    // field in its list.
    if (e.target.closest('.task-detail-agent-permission-mode-select')) {
      const input = e.target;
      const owner = currentState;
      // The Cowboy scope select waits for this save (TPT9164): its reply decides whether the
      // card shows, and both replies are full rows, so overlapping saves could leave the older one.
      // Disabling the focused trigger drops keyboard focus to the body; syncPermissionModeSelect()
      // gives it back when the save re-enables it.
      const cowboy = owner.root.querySelector('.task-detail-cowboy-toggle');
      input.disabled = true;
      if (cowboy) cowboy.disabled = true;
      syncPermissionModeSelect(input);
      patchField('agent_permission_mode', input.value || null, input, owner).then(ok => {
        if (!ok) input.value = owner.task.agent_permission_mode || '';
        input.disabled = false;
        if (cowboy) cowboy.disabled = false;
        syncPermissionModeSelect(input);
        if (currentState === owner) {
          syncPermissionModeHint();
          syncCowboyField();
        }
      });
      return;
    }

    // Cowboy-coding mode (TPT9164) — PATCHes { cowboy_mode } with the Permission mode select
    // disabled until the reply lands (see above). The API re-checks the bypass-only rule against the
    // mode it has stored and answers 200 with cowboy_mode false when the task is not in bypass after
    // all — a project default changed since this drawer loaded — so the card is re-derived from the
    // reply, and a refused "on" says why and drops the cached project for the next open.
    if (e.target.closest('.task-detail-cowboy-toggle')) {
      const scopeSelect = e.target;
      const owner = currentState;
      const wanted = scopeSelect.value;
      const previous = normalizeCowboyScope(owner.task.cowboy_mode);
      const mode = owner.root.querySelector('.task-detail-agent-permission-mode-select');
      // Restore focus after saving unless it moved to another control.
      const hadFocus = document.activeElement === scopeSelect;
      scopeSelect.disabled = true;
      if (mode) { mode.disabled = true; syncPermissionModeSelect(mode); }
      patchField('cowboy_mode', wanted, scopeSelect, owner).then(ok => {
        scopeSelect.disabled = false;
        if (mode) { mode.disabled = false; syncPermissionModeSelect(mode); }
        if (ok && wanted !== 'disabled' && normalizeCowboyScope(owner.task.cowboy_mode) === 'disabled') {
          invalidateProject(owner.projectId);
          showToast(t('cowboyMode.refused'), 'error');
        }
        if (currentState === owner) syncCowboyField();
        else if (!ok) scopeSelect.value = previous;
        if (hadFocus && currentState === owner && document.activeElement === document.body) scopeSelect.focus();
      });
      return;
    }

    // Notifications tab (TPT18): Subscribed mute/follow toggle — optimistic, reverted
    // on failure. Sends a strict boolean; the API 400s on anything else.
    if (e.target.closest('.task-detail-subscribe-toggle')) {
      const checkbox = e.target;
      const next = checkbox.checked;
      const { projectId, taskKey } = currentState;
      api.put('/projects/' + projectId + '/tasks/' + taskKey + '/subscription', { subscribed: next })
        .then((res) => { eventsSubscribed = res.subscribed !== false; })
        .catch(err => {
          checkbox.checked = !next;
          showToast(err.message || 'Failed to update subscription', 'error');
        });
      return;
    }
  };

  const onBlur = (e) => {
    if (!currentState || currentState.root !== e.currentTarget) return;

    // Story points
    if (e.target.closest('.task-detail-sp-input')) {
      const val = e.target.value.trim();
      const sp = val === '' ? null : parseInt(val, 10);
      if (sp !== currentState.task.story_points) {
        patchField('story_points', sp);
      }
    }

    // Due date (TPT589): a date left half-typed goes back to the saved one; a complete one is
    // queued here too, for browsers that only fire `change` once the field loses focus.
    if (e.target.closest('.task-detail-due-input')) {
      if (dueInputValue(e.target) === undefined) e.target.value = currentState.task.due_date || '';
      else queueDueSave(e.target);
    }
  };

  const unbindPermissionModes = bindPermissionModeSelects(el);
  const unbindDevLinks = bindDevLinks(el, () => currentState?.root === el ? currentState : null, api);
  el.addEventListener('click', onClick);
  el.addEventListener('change', onChange);
  el.addEventListener('blur', onBlur, true); // capture — inputs don't bubble blur

  return () => {
    unbindPermissionModes();
    unbindDevLinks();
    el.removeEventListener('click', onClick);
    el.removeEventListener('change', onChange);
    el.removeEventListener('blur', onBlur, true);
  };
}

// options.focusCard / options.focusBoard (TPT378) — the clicked board card and the board
// element it sits in. When both are given the card stays focused (ringed, sharp) and the rest
// of the board is blurred until the drawer closes; every other caller omits them.
//
// Returns the drawer's owner token synchronously (TPT647) — the page keeps it and passes it to
// closeTaskDetail() in its teardown. The task itself loads in the background.
export function openTaskDetail(projectId, taskKey, options = {}) {
  return openPanel(projectId, taskKey, options, {});
}

// Opens the drawer under `token`: a fresh one from openTaskDetail(), or the current owner's when
// the drawer navigates to a parent/child task.
function openPanel(projectId, taskKey, options, token) {
  ensurePanel();
  panelSeq++;
  detailOwner = token;
  applyBoardFocus(options.focusCard, options.focusBoard);
  // The phone layout applies before the first paint and follows the viewport while open (TPT9214).
  bindMobileEditorLayout(true);
  syncMobileEditorLayout();

  // Show loading state
  panelEl.innerHTML = '<div class="task-detail-body"><div class="empty"><span class="spinner"></span></div></div>';
  // The compact drawer is its own scroller: a newly opened task starts at its top.
  panelEl.scrollTop = 0;
  overlayEl.classList.add('open');
  panelEl.classList.add('open');
  document.body.classList.add('task-detail-open');

  // Bind escape
  if (escHandler) document.removeEventListener('keydown', escHandler);
  escHandler = (e) => {
    if (e.key === 'Escape' && !document.querySelector('.modal-overlay')) {
      closeTaskDetail(token);
    }
  };
  document.addEventListener('keydown', escHandler);

  loadPanel(projectId, taskKey, options, token);
  return token;
}

async function loadPanel(projectId, taskKey, options, token) {
  const seq = panelSeq;
  comments = [];
  taskEvents = [];
  eventsSubscribed = true;
  activeTab = 'details';
  observedEventIds = new Set();
  editingCommentId = null;
  if (editCommentMentionTypeahead) { editCommentMentionTypeahead.destroy(); editCommentMentionTypeahead = null; }
  try {
    const [taskRes, membersRes, commentsRes, sprintsRes, statuses, project, devices, eventsRes, fetchedChildren, fetchedSprintRows] = await Promise.all([
      // The task, then its parent when options.allTasks lacks it (TPT618) — chained onto this
      // leg so the one-row parent read overlaps the legs below instead of following them.
      api.get('/projects/' + projectId + '/tasks/' + taskKey)
        .then(async (res) => { await loadParentContext(projectId, res.task, options); return res; }),
      api.get('/projects/' + projectId + '/members'),
      api.get('/projects/' + projectId + '/tasks/' + taskKey + '/comments').catch(() => ({ comments: [] })),
      api.get('/projects/' + projectId + '/sprints').catch(() => ({ sprints: [] })),
      loadStatuses(projectId),
      // C1334/C1395 — sprints_enabled gates the Sprint field below, task_group_label names
      // it. Self-fetched rather than threaded through options: this module is opened from 6
      // panel call sites plus the standalone page, and every one would have to pass it.
      // Via the shared per-project cache (lib/project.js, C1395) rather than a fresh fetch
      // per open — a Settings-tab save calls invalidateProject() itself, so this still never
      // shows a stale label/toggle past the save that changed it. Fails open to `null`,
      // which sprintsEnabled() reads as ON — never hides a live field over a blip.
      loadProject(projectId),
      // C1389 — gates the "Start in Task App" button (is_objective tasks only, see
      // renderPanelContent()). Both entry points fetch this for the same reason
      // loadStatuses()/loadProject() are fetched by both — see § Two Surfaces, One
      // Renderer. Fails open to [] (lib/devices.js), so a devices hiccup only disables
      // the button, never blocks the panel.
      loadDevices(),
      // (TPT10 backend, TPT18 first web caller) — Notifications tab event log + this
      // user's effective subscribed state. Fails open (empty log, subscribed default-on)
      // so an events hiccup never blocks the rest of the panel, same discipline as the
      // comments/sprints legs above.
      api.get('/projects/' + projectId + '/tasks/' + taskKey + '/events?limit=100').catch(() => ({ events: [], total: 0, subscribed: true })),
      // (TPT628) The children by the task's own key — a board's options.allTasks is its
      // filtered page, so filtering it would hide every child the filter excludes. Soft-fails
      // to null, and detailChildren() then falls back to the caller's rows.
      loadChildren(projectId, taskKey),
      // (TPT641) Project-wide sprint numbers for a caller that passed no options.sprintRows
      // (Epics, Sprint Planning, Workspace Tasks) — this task's own projectId, so a cross-project
      // page gets the right project. No request when the caller supplied them; null on failure.
      loadSprintRows(projectId, options),
    ]);

    // A close or a newer open (another page's, or this drawer's own parent/child navigation)
    // superseded this load — the drawer is no longer showing this task.
    if (detailOwner !== token || panelSeq !== seq) return;
    const task = taskRes.task;
    const members = membersRes.members || [];
    const children = detailChildren(fetchedChildren, options.allTasks, task.id);
    comments = commentsRes.comments || [];
    taskEvents = eventsRes.events || [];
    eventsSubscribed = eventsRes.subscribed !== false;

    options.sprints = sprintsRes.sprints || [];
    // children on currentState, never on options: child/parent navigation hands options on to
    // the next openTaskDetail(), which loads that task's own children.
    // sprintRows on currentState, never on options, for the same reason: each open refetches
    // them, and a caller-supplied list stays distinguishable from a self-fetched one.
    currentState = { projectId, taskKey, task, members, children, options, sprintRows: fetchedSprintRows, statuses: statuses || [], project, devices: devices || [], root: panelEl, mode: 'panel' };
    startRemoteControls(currentState);

    panelEl.innerHTML = renderPanelContent(task, members, children);
    const commentInput = panelEl.querySelector('.task-detail-comment-input');
    if (commentInput) attachImageHandlers(commentInput, projectId, taskKey);
    mountAssigneeCombo();
    mountTagTypeahead();
    mountDependencyTypeahead();
    mountMentionTypeaheads();
    loadDependencyInfo(currentState);
  } catch (err) {
    if (detailOwner !== token || panelSeq !== seq) return;
    panelEl.innerHTML = `<div class="task-detail-body"><div class="empty text-muted">Failed to load task: ${escapeHtml(err.message)}</div></div>`;
  }
}

// With a token (TPT647) it acts only while that token owns the drawer (null never does) — a late
// page teardown leaves the drawer of the page now on screen alone. No argument closes whatever
// drawer is open.
export function closeTaskDetail(token) {
  if (!panelEl || !overlayEl) return;
  if (token !== undefined && token !== detailOwner) return;
  detailOwner = null;
  // The drawer's own modal (Select Parent, Delete Task, Add Subtask, remote terminal) goes with it.
  closeModal(detailModalToken);
  detailModalToken = null;
  if (attributionView?.root === panelEl) stopRemoteControls();
  if (assigneeCombo) { assigneeCombo.destroy(); assigneeCombo = null; }
  if (tagTypeahead) { tagTypeahead.destroy(); tagTypeahead = null; }
  if (depTypeahead) { depTypeahead.destroy(); depTypeahead = null; }
  if (commentMentionTypeahead) { commentMentionTypeahead.destroy(); commentMentionTypeahead = null; }
  if (editCommentMentionTypeahead) { editCommentMentionTypeahead.destroy(); editCommentMentionTypeahead = null; }
  editingCommentId = null;
  // Closing doesn't remove panelEl from the DOM (it slides out via CSS class), so a
  // textarea mid-edit never blurs here — its {once:true} blur -> save() (which would
  // destroy the embed block) simply never fires. The next openTaskDetail() overwrites
  // panelEl.innerHTML directly, which would leak any open Embed menu's document-level
  // mousedown/keydown listeners without this explicit close (C1447). Same reasoning for
  // the Notifications tab's IntersectionObserver (TPT18) — it would otherwise keep
  // observing rows that the next openTaskDetail() has already thrown away — and for the
  // mention typeahead (TPT33): a stray open dropdown otherwise outlives the panel.
  closeAnyOpenEmbedMenu();
  closeAnyOpenMentionTypeahead();
  disconnectEventObserver();
  // Before the ownership early-return below — the blur must lift with the slide-out even
  // when a standalone page owns currentState (TPT378).
  releaseBoardFocus();
  panelEl.classList.remove('open');
  overlayEl.classList.remove('open');
  document.body.classList.remove('task-detail-open');
  bindMobileEditorLayout(false);

  if (escHandler) {
    document.removeEventListener('keydown', escHandler);
    escHandler = null;
  }

  // Only this panel's own ownership of currentState is ours to clear — a
  // standalone task page may currently own it (mode: 'page'), unrelated to
  // this panel's chrome being closed.
  if (!currentState || currentState.root !== panelEl) return;

  // Escape never blurs the due date input (the panel only slides off-screen), so a typed date a
  // blur-only browser has not committed with `change` yet is queued here (TPT589).
  const dueInput = panelEl.querySelector('.task-detail-due-input');
  if (dueInput) queueDueSave(dueInput, currentState);

  const onClose = currentState.options.onClose;
  currentState = null;

  // Call onClose after transition — but only if no other panel opened in the meantime
  // (see panelSeq comment above the declaration): a re-render triggered by this onClose
  // (C1261) would otherwise force-close a drawer the user opened right after this one.
  // It also waits for the due date saves still queued (TPT589), so the owner's reload reads the
  // final date.
  const seq = panelSeq;
  if (onClose) {
    setTimeout(() => { dueSaveChain.then(() => { if (panelSeq === seq) onClose(); }); }, 220);
  }
}

// mountTaskDetail(hostEl, projectId, taskKey, options) — standalone-page counterpart
// to openTaskDetail(). A sibling, not a refactor of it: openTaskDetail flips on the
// panel's slide-in classes BEFORE the fetch resolves so the animation starts
// immediately, which is panel-chrome-specific and doesn't apply to a page host.
// Rethrows on failure so the caller (taskDetailPage) owns its own error UI —
// it knows the project id for a "back to board" link, this module doesn't.
// Resolves to the unmount fn. Resolves to null instead when a navigation superseded the mount
// during its fetch (router.js getNavigationId()): it then releases what it bound before that
// await and writes, renders and mounts nothing, so there is nothing left for the caller to
// release. A failure after such a navigation resolves null too, never throws.
export async function mountTaskDetail(hostEl, projectId, taskKey, options = {}) {
  // Read before the first await (tt-web-router § Page-side mount rule).
  const navId = getNavigationId();
  const superseded = () => getNavigationId() !== navId;
  // renderPanelContent()/refreshPanelContent() only ever rewrite hostEl.innerHTML, never its
  // className — the page-mode styling hook (.task-detail-page, styles.css) has to be asserted
  // here instead of left to whatever class the caller's host element happened to be given.
  hostEl.classList.add('task-detail-page');
  hostEl.innerHTML = '<div class="task-detail-body"><div class="empty"><span class="spinner"></span></div></div>';
  const unbind = bindDetailEvents(hostEl);
  // Everything this mount binds or asserts on hostEl before its await.
  const release = () => {
    unbind();
    hostEl.classList.remove('task-detail-page');
  };
  comments = [];
  taskEvents = [];
  eventsSubscribed = true;
  activeTab = 'details';
  observedEventIds = new Set();
  editingCommentId = null;
  if (editCommentMentionTypeahead) { editCommentMentionTypeahead.destroy(); editCommentMentionTypeahead = null; }

  try {
    const [taskRes, membersRes, commentsRes, sprintsRes, statuses, project, devices, eventsRes] = await Promise.all([
      // Same chained task + parent leg as openTaskDetail() above (TPT618). The page's
      // options.allTasks holds only the task's children, so a subtask's parent always comes
      // from this one-row read.
      api.get('/projects/' + projectId + '/tasks/' + taskKey)
        .then(async (res) => { await loadParentContext(projectId, res.task, options); return res; }),
      api.get('/projects/' + projectId + '/members'),
      api.get('/projects/' + projectId + '/tasks/' + taskKey + '/comments').catch(() => ({ comments: [] })),
      api.get('/projects/' + projectId + '/sprints').catch(() => ({ sprints: [] })),
      loadStatuses(projectId),
      // C1334/C1395 — same leg as openTaskDetail() above; both entry points share
      // renderPanelContent() and must agree on whether the Sprint field is shown and named.
      loadProject(projectId),
      // C1389 — same leg as openTaskDetail() above; both must agree on button visibility.
      loadDevices(),
      // (TPT10 backend, TPT18 first web caller) — same leg as openTaskDetail() above;
      // both entry points must agree on the Notifications tab's content/badge.
      api.get('/projects/' + projectId + '/tasks/' + taskKey + '/events?limit=100').catch(() => ({ events: [], total: 0, subscribed: true })),
    ]);
    if (superseded()) {
      release();
      return null;
    }

    const task = taskRes.task;
    const members = membersRes.members || [];
    comments = commentsRes.comments || [];
    taskEvents = eventsRes.events || [];
    eventsSubscribed = eventsRes.subscribed !== false;
    options.sprints = sprintsRes.sprints || [];

    // The page prefetched the children (GET /tasks/:taskKey/children) as options.allTasks.
    const children = detailChildren(null, options.allTasks, task.id);
    currentState = { projectId, taskKey, task, members, children, options, statuses: statuses || [], project, devices: devices || [], root: hostEl, mode: 'page' };
    startRemoteControls(currentState);

    hostEl.innerHTML = renderPanelContent(task, members, children);
    const commentInput = hostEl.querySelector('.task-detail-comment-input');
    if (commentInput) attachImageHandlers(commentInput, projectId, taskKey);
    mountAssigneeCombo();
    mountTagTypeahead();
    mountDependencyTypeahead();
    mountMentionTypeaheads();
    loadDependencyInfo(currentState);
  } catch (err) {
    if (superseded()) {
      release();
      return null;
    }
    unbind();
    throw err;
  }

  return () => {
    // A typed due date a blur-only browser has not committed yet (TPT589) — see closeTaskDetail().
    if (currentState && currentState.root === hostEl) {
      const dueInput = hostEl.querySelector('.task-detail-due-input');
      if (dueInput) queueDueSave(dueInput, currentState);
    }
    unbind();
    disconnectEventObserver();
    closeAnyOpenMentionTypeahead();
    if (assigneeCombo) { assigneeCombo.destroy(); assigneeCombo = null; }
    if (tagTypeahead) { tagTypeahead.destroy(); tagTypeahead = null; }
    if (depTypeahead) { depTypeahead.destroy(); depTypeahead = null; }
    if (commentMentionTypeahead) { commentMentionTypeahead.destroy(); commentMentionTypeahead = null; }
    if (editCommentMentionTypeahead) { editCommentMentionTypeahead.destroy(); editCommentMentionTypeahead = null; }
    editingCommentId = null;
    // The page's own modal (Delete Task, Select Parent, Add Subtask, remote terminal) goes with it
    // (TPT647).
    closeModal(detailModalToken);
    detailModalToken = null;
    if (attributionView?.root === hostEl) stopRemoteControls();
    hostEl.classList.remove('task-detail-page');
    if (currentState && currentState.root === hostEl) currentState = null;
  };
}

// Init panel + bind events on module load (delegation — safe before content exists)
ensurePanel();
bindDetailEvents(panelEl);
